
import { ChangeEvent, Fragment, KeyboardEvent as ReactKeyboardEvent, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import Konva from 'konva'
import { Circle, Group, Layer, Line, Rect, Stage, Text, Transformer } from 'react-konva'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/app/components/ui/alert-dialog'
import { GEAR_DEFINITIONS, GEAR_PALETTE, getGearSnapPoints, SPAN_GEAR_TYPES, getDescendantGears, findNearestParentGearSnap, computeChildPosition } from './gear'
import { GEAR_TO_ICON } from './gear-icon'
import { ICON_MAP } from './icons'
import { BasicShapeElement, Calibration, DrawingElement, FreedrawElement, GearElement, GearType, Point, RopeElement, RopeEnd, RopeHandleDrag, RopeHandleRef, RopeRole, SnapCandidate, SNAP_DISTANCE, SNAP_DROP_DISTANCE, Tool } from './types'
import { BasicShapeNode, GearNode, isAdditive, MeasurementLayer, RopeNode, SnapHighlight, WarningLayer } from './rigging-elements'
import { findBestSegmentIndex, findNearestGearSnap, findNearestSnap, getRopeControlPoints, snapCandidateToRopeEnd } from './rope-geometry'
import { insertRopeContactAt, removeRopeContact, setRopeContact, setRopeEndpoint } from './rope-edit'
import { useHistory } from './use-history'
import {
  boxIntersects,
  BoundingBox,
  deleteElementsByIds,
  getContentBoundingBox,
  getElementBoundingBox,
  getExportBounds,
  normalizeMarquee,
} from './element-ops'
import {
  canRecoverDiagramDraft,
  clearDiagramStorage,
  diagramSignature,
  DIAGRAM_SCHEMA_VERSION,
  downloadFile,
  loadCalibrationFromStorage,
  loadDiagramFileFromStorage,
  parseDiagramFile,
  saveCalibrationToStorage,
  saveDiagramToStorage,
  serializeDiagram,
  timestampedName,
} from './persistence'
import type { DiagramFile } from './persistence'
import { formatLength, niceScaleBar } from './measurements'
import { DEFAULT_ROPE_ROLE, getRopeRoleStyle, isRoleDefaultColor, ROPE_ROLES } from './rope-roles'
import { RiggingLegend } from './rigging-legend'
import { GEAR_SUBTYPE_CATALOG, GEAR_TYPES_WITH_SUBTYPES } from './gear-subtypes'

type DraftRope = {
  start: Point
  current: Point
  startSnap: SnapCandidate | null
  currentSnap: SnapCandidate | null
}

type DraftShape = {
  id: string
  tool: Exclude<Tool, 'select' | 'rope' | 'freedraw'>
  start: Point
  current: Point
}

type DraftFreedraw = {
  id: string
  points: number[]
}

type Marquee = {
  start: Point
  current: Point
  additive: boolean
  baseSelection: string[]
}

type Viewport = {
  x: number
  y: number
  scale: number
}

const TOOL_LABELS: Array<{ tool: Tool; label: string }> = [
  { tool: 'select', label: 'Select' },
  { tool: 'freedraw', label: 'Pen' },
  { tool: 'rope', label: 'Rope' },
  { tool: 'arrow', label: 'Arrow' },
  { tool: 'rect', label: 'Rect' },
  { tool: 'ellipse', label: 'Circle' },
  { tool: 'text', label: 'Text' },
]

const initialElements: DrawingElement[] = []

// Default footprint for a placed gear icon (square gear types).
export const DEFAULT_GEAR_SIZE = 72

function makeId(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
}

const MENUBAR_HEIGHT = 32
const DEFAULT_RIBBON_HEIGHT = 64

// Maximum bounding-box extent (px) for a placed template.  Templates whose
// raw layout exceeds this are scaled down uniformly to fit.
const MAX_TEMPLATE_EXTENT = 400

function viewportCenterWorld(viewport: Viewport, containerWidth: number, containerHeight: number): Point {
  const centerScreenX = containerWidth / 2
  const centerScreenY = containerHeight / 2
  return {
    x: (centerScreenX - viewport.x) / viewport.scale,
    y: (centerScreenY - viewport.y) / viewport.scale,
  }
}

type TemplateDefinition = {
  id: string
  label: string
  description: string
  build: (baseX: number, baseY: number) => DrawingElement[]
}

const TEMPLATES: TemplateDefinition[] = [
  {
    id: 'twin-ropes',
    label: 'Twin Rope (Working + Safety)',
    description: 'Two independent anchors: working line + backup safety line',
    build: (bx, by) => {
      const anchor1Id = makeId('anchor')
      const anchor2Id = makeId('anchor')
      const rope1Id = makeId('rope')
      const rope2Id = makeId('rope')
      const working = getRopeRoleStyle('working')
      const safety = getRopeRoleStyle('safety')
      return [
        { id: anchor1Id, kind: 'gear', gearType: 'anchor', x: bx, y: by, width: DEFAULT_GEAR_SIZE, height: DEFAULT_GEAR_SIZE, rotation: 0 },
        { id: anchor2Id, kind: 'gear', gearType: 'anchor', x: bx + 120, y: by, width: DEFAULT_GEAR_SIZE, height: DEFAULT_GEAR_SIZE, rotation: 0 },
        {
          id: rope1Id, kind: 'rope', role: 'working',
          start: { mode: 'attached', elementId: anchor1Id, snapPointId: 'bottom' },
          end: { mode: 'free', x: bx + DEFAULT_GEAR_SIZE / 2, y: by + 100, fallsToGround: true },
          contacts: [], color: working?.color ?? '#dc2626', diameter: 5,
        },
        {
          id: rope2Id, kind: 'rope', role: 'safety',
          start: { mode: 'attached', elementId: anchor2Id, snapPointId: 'bottom' },
          end: { mode: 'free', x: bx + 120 + DEFAULT_GEAR_SIZE / 2, y: by + 100, fallsToGround: true },
          contacts: [], color: safety?.color ?? '#2563eb', diameter: 5,
        },
      ]
    },
  },
  {
    id: 'tension-line',
    label: 'Tension Line',
    description: 'Taut working line between two anchors',
    build: (bx, by) => {
      const leftAnchorId = makeId('anchor')
      const rightAnchorId = makeId('anchor')
      const ropeId = makeId('rope')
      const working = getRopeRoleStyle('working')
      return [
        { id: leftAnchorId, kind: 'gear', gearType: 'anchor', x: bx, y: by, width: DEFAULT_GEAR_SIZE, height: DEFAULT_GEAR_SIZE, rotation: 0 },
        { id: rightAnchorId, kind: 'gear', gearType: 'anchor', x: bx + 350, y: by, width: DEFAULT_GEAR_SIZE, height: DEFAULT_GEAR_SIZE, rotation: 0 },
        {
          id: ropeId, kind: 'rope', role: 'working',
          start: { mode: 'attached', elementId: leftAnchorId, snapPointId: 'bottom' },
          end: { mode: 'attached', elementId: rightAnchorId, snapPointId: 'bottom' },
          contacts: [], color: working?.color ?? '#dc2626', diameter: 5, sagMultiplier: 0,
        },
      ]
    },
  },
  {
    id: 're-anchor-setup',
    label: 'Re-anchor Setup',
    description: 'Working line redirected through a re-anchor',
    build: (bx, by) => {
      const anchorId = makeId('anchor')
      const reAnchorId = makeId('re-anchor')
      const ropeId = makeId('rope')
      const working = getRopeRoleStyle('working')
      return [
        { id: anchorId, kind: 'gear', gearType: 'anchor', x: bx, y: by, width: DEFAULT_GEAR_SIZE, height: DEFAULT_GEAR_SIZE, rotation: 0 },
        { id: reAnchorId, kind: 'gear', gearType: 're-anchor', x: bx + 200, y: by, width: 140, height: DEFAULT_GEAR_SIZE, rotation: 0 },
        {
          id: ropeId, kind: 'rope', role: 'working',
          start: { mode: 'attached', elementId: anchorId, snapPointId: 'bottom' },
          end: { mode: 'attached', elementId: reAnchorId, snapPointId: 'bottom' },
          contacts: [],
          color: working?.color ?? '#dc2626', diameter: 5, autoSag: true,
        },
      ]
    },
  },
  {
    id: 'srt-access',
    label: 'Rope Access (SRT)',
    description: 'Technician on a working line + independent safety line',
    build: (bx, by) => {
      const workAnchorId = makeId('anchor')
      const safeAnchorId = makeId('anchor')
      const techId = makeId('technician')
      const workRopeId = makeId('rope')
      const safeRopeId = makeId('rope')
      const working = getRopeRoleStyle('working')
      const safety = getRopeRoleStyle('safety')
      const techX = bx + 40
      const techY = by + 320
      return [
        { id: workAnchorId, kind: 'gear', gearType: 'anchor', x: bx, y: by, width: DEFAULT_GEAR_SIZE, height: DEFAULT_GEAR_SIZE, rotation: 0, label: 'Working anchor' },
        { id: safeAnchorId, kind: 'gear', gearType: 'anchor', x: bx + 120, y: by, width: DEFAULT_GEAR_SIZE, height: DEFAULT_GEAR_SIZE, rotation: 0, label: 'Safety anchor' },
        { id: techId, kind: 'gear', gearType: 'technician', x: techX, y: techY, width: DEFAULT_GEAR_SIZE, height: DEFAULT_GEAR_SIZE, rotation: 0 },
        {
          id: workRopeId, kind: 'rope', role: 'working',
          start: { mode: 'attached', elementId: workAnchorId, snapPointId: 'bottom' },
          end: { mode: 'attached', elementId: techId, snapPointId: 'harness' },
          contacts: [], color: working?.color ?? '#dc2626', diameter: 5, sagMultiplier: 0,
        },
        {
          id: safeRopeId, kind: 'rope', role: 'safety',
          start: { mode: 'attached', elementId: safeAnchorId, snapPointId: 'bottom' },
          end: { mode: 'attached', elementId: techId, snapPointId: 'harness' },
          contacts: [], color: safety?.color ?? '#2563eb', diameter: 5, sagMultiplier: 0,
        },
      ]
    },
  },
  {
    id: 'deviation-redirect',
    label: 'Deviation',
    description: 'Working line pulled aside by a deviation anchor',
    build: (bx, by) => {
      const anchorId = makeId('anchor')
      const deviationId = makeId('deviation')
      const ropeId = makeId('rope')
      const working = getRopeRoleStyle('working')
      return [
        { id: anchorId, kind: 'gear', gearType: 'anchor', x: bx, y: by, width: DEFAULT_GEAR_SIZE, height: DEFAULT_GEAR_SIZE, rotation: 0 },
        { id: deviationId, kind: 'gear', gearType: 'deviation', x: bx - 110, y: by + 200, width: DEFAULT_GEAR_SIZE, height: DEFAULT_GEAR_SIZE, rotation: 0 },
        {
          id: ropeId, kind: 'rope', role: 'working',
          start: { mode: 'attached', elementId: anchorId, snapPointId: 'bottom' },
          end: { mode: 'free', x: bx + DEFAULT_GEAR_SIZE / 2, y: by + 420, fallsToGround: true },
          contacts: [{ elementId: deviationId, snapPointId: 'contact', contactType: 'deviation' }],
          color: working?.color ?? '#dc2626', diameter: 5,
        },
      ]
    },
  },
  {
    id: 'hauling-line',
    label: 'Hauling Line',
    description: 'Anchored pulley raising a load (layout only)',
    build: (bx, by) => {
      const anchorId = makeId('anchor')
      const pulleyId = makeId('pulley')
      const loadId = makeId('load')
      const haul = getRopeRoleStyle('haul')
      return [
        { id: anchorId, kind: 'gear', gearType: 'anchor', x: bx, y: by, width: DEFAULT_GEAR_SIZE, height: DEFAULT_GEAR_SIZE, rotation: 0 },
        { id: pulleyId, kind: 'gear', gearType: 'pulley', x: bx + 6, y: by + 70, width: DEFAULT_GEAR_SIZE, height: DEFAULT_GEAR_SIZE, rotation: 0 },
        { id: loadId, kind: 'gear', gearType: 'load', x: bx + 160, y: by + 320, width: DEFAULT_GEAR_SIZE, height: DEFAULT_GEAR_SIZE, rotation: 0 },
        {
          id: makeId('rope'), kind: 'rope', role: 'haul',
          start: { mode: 'attached', elementId: loadId, snapPointId: 'attachment' },
          end: { mode: 'free', x: bx + DEFAULT_GEAR_SIZE / 2 + 120, y: by + 480, fallsToGround: false },
          contacts: [{ elementId: pulleyId, snapPointId: 'sheave', contactType: 'pulley' }],
          color: haul?.color ?? '#ea580c', diameter: 5,
        },
      ]
    },
  },
]

function getPointer(stageRef: RefObject<Konva.Stage | null>, viewport: Viewport): Point | null {
  const stage = stageRef.current
  const pos = stage?.getPointerPosition()
  if (!pos) return null
  return {
    x: (pos.x - viewport.x) / viewport.scale,
    y: (pos.y - viewport.y) / viewport.scale,
  }
}

function getGearElements(elements: DrawingElement[]): GearElement[] {
  return elements.filter((element): element is GearElement => element.kind === 'gear')
}

/**
 * Spread (px) and midpoint of the first two active touches, in stage-container
 * (screen) coordinates. Used to drive two-finger pinch-zoom and pan on tablets.
 */
function getTwoTouchGesture(
  stageRef: RefObject<Konva.Stage | null>,
  touches: TouchList,
): { distance: number; center: Point } | null {
  const container = stageRef.current?.container()
  if (!container || touches.length < 2) return null
  const rect = container.getBoundingClientRect()
  const a = { x: touches[0].clientX - rect.left, y: touches[0].clientY - rect.top }
  const b = { x: touches[1].clientX - rect.left, y: touches[1].clientY - rect.top }
  return {
    distance: Math.hypot(b.x - a.x, b.y - a.y),
    center: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
  }
}

function normalizeShapeDraft(draft: DraftShape, id: string): BasicShapeElement {
  const dx = draft.current.x - draft.start.x
  const dy = draft.current.y - draft.start.y

  if (draft.tool === 'rect' || draft.tool === 'ellipse') {
    return {
      id,
      kind: 'shape',
      shapeType: draft.tool,
      x: Math.min(draft.start.x, draft.current.x),
      y: Math.min(draft.start.y, draft.current.y),
      width: Math.max(Math.abs(dx), 1),
      height: Math.max(Math.abs(dy), 1),
      rotation: 0,
      stroke: '#334155',
      fill: 'rgba(148,163,184,0.08)',
    }
  }

  if (draft.tool === 'text') {
    return {
      id,
      kind: 'shape',
      shapeType: 'text',
      x: draft.start.x,
      y: draft.start.y,
      width: Math.max(Math.abs(dx), 120),
      height: Math.max(Math.abs(dy), 28),
      rotation: 0,
      text: 'Text',
      fontSize: 20,
      stroke: '#334155',
    }
  }

  if (draft.tool === 'i-beam') {
    return {
      id,
      kind: 'shape',
      shapeType: 'i-beam',
      x: draft.start.x,
      y: draft.start.y,
      width: dx,
      height: dy,
      rotation: 0,
      stroke: '#6B7280',
    }
  }

  return {
    id,
    kind: 'shape',
    shapeType: draft.tool,
    x: draft.start.x,
    y: draft.start.y,
    width: dx,
    height: dy,
    rotation: 0,
    stroke: '#334155',
  }
}

function roundTo(value: number, decimals: number): number {
  const factor = Math.pow(10, decimals)
  return Math.round(value * factor) / factor
}

function computeMarqueeSelection(
  elements: DrawingElement[],
  gears: GearElement[],
  marqueeBox: BoundingBox,
): string[] {
  const ids: string[] = []
  for (const element of elements) {
    const bbox = getElementBoundingBox(element, gears)
    if (!bbox) continue
    if (boxIntersects(bbox, marqueeBox)) ids.push(element.id)
  }
  return ids
}

function uniqueIds(ids: string[]): string[] {
  return Array.from(new Set(ids))
}

export type RiggingDrawCanvasProps = {
  /** Seed diagram loaded on mount (replaces localStorage hydration). */
  initialValue?: DiagramFile | null
  /**
   * Ref whose `.current` is called whenever the diagram mutates so the parent
   * can persist / lift-state without re-rendering the canvas.
   */
  onChangeRef?: RefObject<((next: DiagramFile) => void) | null>
  /** Ref populated with a function that returns a PNG data-URL of the stage. */
  exportPngRef?: RefObject<(() => string | null) | null>
  /** Stable document-scoped key for local draft recovery. */
  draftKey?: string
  /** Signature of the latest diagram state confirmed by the backend. */
  persistedSignature?: string | null
  /** Immutable latest revision number confirmed by the backend. */
  persistedRevision?: number | null
  /** True while an existing diagram's backend value is still loading. */
  loading?: boolean
}

export default function RiggingDrawCanvas({
  initialValue,
  onChangeRef,
  exportPngRef,
  draftKey,
  persistedSignature,
  persistedRevision,
  loading = false,
}: RiggingDrawCanvasProps = {}) {
  const stageRef = useRef<Konva.Stage>(null)
  const bgLayerRef = useRef<Konva.Layer>(null)
  const contentGroupRef = useRef<Konva.Group>(null)
  const overlayGroupRef = useRef<Konva.Group>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const ribbonRef = useRef<HTMLDivElement>(null)
  const [containerSize, setContainerSize] = useState<{ width: number; height: number }>({ width: 1024, height: 768 })
  const [ribbonHeight, setRibbonHeight] = useState(DEFAULT_RIBBON_HEIGHT)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect
        if (width > 0 && height > 0) {
          setContainerSize({ width, height })
        }
      }
    })
    ro.observe(el)
    // seed initial size
    const rect = el.getBoundingClientRect()
    if (rect.width > 0 && rect.height > 0) {
      setContainerSize({ width: rect.width, height: rect.height })
    }
    return () => ro.disconnect()
  }, [])
  useEffect(() => {
    const ribbon = ribbonRef.current
    if (!ribbon) return
    const updateHeight = () => setRibbonHeight(Math.max(DEFAULT_RIBBON_HEIGHT, Math.ceil(ribbon.getBoundingClientRect().height)))
    updateHeight()
    const observer = new ResizeObserver(updateHeight)
    observer.observe(ribbon)
    return () => observer.disconnect()
  }, [])
  const history = useHistory<DrawingElement[]>(initialElements)
  const resetHistory = history.reset
  const elements = history.present
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [tool, setTool] = useState<Tool>('select')
  const [viewport, setViewport] = useState<Viewport>({ x: 0, y: 0, scale: 1 })
  const [draftRope, setDraftRope] = useState<DraftRope | null>(null)
  const [ropeRole, setRopeRole] = useState<RopeRole>(DEFAULT_ROPE_ROLE)
  const [draftShape, setDraftShape] = useState<DraftShape | null>(null)
  const [draftFreedraw, setDraftFreedraw] = useState<DraftFreedraw | null>(null)
  const [marquee, setMarquee] = useState<Marquee | null>(null)
  const [snapCandidate, setSnapCandidate] = useState<SnapCandidate | null>(null)
  const [ropeHandleDrag, setRopeHandleDrag] = useState<RopeHandleDrag | null>(null)
  const [isPanning, setIsPanning] = useState(false)
  const [showHelp, setShowHelp] = useState(false)
  const [panStart, setPanStart] = useState<{ pointer: Point; viewport: Viewport } | null>(null)
  // Active two-finger pinch/pan gesture (touch / tablet). Tracks the gesture's
  // starting finger spread and midpoint so zoom + pan can be applied together.
  const pinchRef = useRef<{ distance: number; center: Point; viewport: Viewport } | null>(null)
  const [calibration, setCalibration] = useState<Calibration | null>(null)
  const [scaleMode, setScaleMode] = useState(false)
  const [draftScale, setDraftScale] = useState<{ start: Point; current: Point } | null>(null)
  const showMeasurements = !!calibration
  const [showLegend, setShowLegend] = useState(false)
  const [nextLegendNumber, setNextLegendNumber] = useState(1)
  const nextLegendNumberRef = useRef(nextLegendNumber)
  nextLegendNumberRef.current = nextLegendNumber
  const [showWarnings, setShowWarnings] = useState(true)
  const scaleModeRef = useRef(scaleMode)
  scaleModeRef.current = scaleMode
  const draftScaleRef = useRef(draftScale)
  draftScaleRef.current = draftScale
  const dragSnapshotRef = useRef<DrawingElement[] | null>(null)
  const dragOriginsRef = useRef<Map<string, Point>>(new Map())
  // Free rope ends whose opposite end is attached to a gear being dragged, so
  // the whole rope translates rigidly with its anchor instead of pivoting.
  const ropeFollowOriginsRef = useRef<Map<string, { start?: Point; end?: Point }>>(new Map())
  const transformSnapshotRef = useRef<DrawingElement[] | null>(null)
  const endpointSnapshotRef = useRef<DrawingElement[] | null>(null)
  const nodeRefs = useRef<Map<string, Konva.Node>>(new Map())
  const transformerRef = useRef<Konva.Transformer>(null)
  const [editingText, setEditingText] = useState<{ id: string; value: string } | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const [openMenu, setOpenMenu] = useState<string | null>(null)
  const [gearSubtypeMenu, setGearSubtypeMenu] = useState<GearType | null>(null)
  const [showRopeRoleMenu, setShowRopeRoleMenu] = useState(false)
  const [showProps, setShowProps] = useState(false)
  const [deleteAllAction, setDeleteAllAction] = useState<'canvas' | 'selection' | null>(null)
  const presentRef = useRef(elements)
  presentRef.current = elements
  const selectedIdsRef = useRef(selectedIds)
  selectedIdsRef.current = selectedIds
  const ropeRoleRef = useRef(ropeRole)
  ropeRoleRef.current = ropeRole
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const hydratedRef = useRef(false)
  const hydratedInitialValueRef = useRef<DiagramFile | null | undefined>(undefined)
  const baselineSignatureRef = useRef<string | null>(null)
  const baseSignatureRef = useRef(diagramSignature([], 1))
  const baseRevisionRef = useRef<number | null>(null)
  const suppressedSignatureRef = useRef<string | null>(null)

  const registerNodeRef = useCallback((id: string, node: Konva.Node | null) => {
    if (node) nodeRefs.current.set(id, node)
    else nodeRefs.current.delete(id)
  }, [])

  const gears = useMemo(() => getGearElements(elements), [elements])

  // Disable native touch gestures on the canvas so two-finger pinch/pan drives
  // the viewport instead of the browser zooming/scrolling the page (tablet build).
  useEffect(() => {
    const container = stageRef.current?.container()
    if (container) container.style.touchAction = 'none'
  }, [])

  useEffect(() => {
    if (loading) return
    if (hydratedRef.current && initialValue === hydratedInitialValueRef.current) return

    let seededElements: DrawingElement[] = []
    let seededLegendNumber = 1
    let seededCalibration: Calibration | null = null
    const backendSignature = initialValue
      ? diagramSignature(initialValue.elements, initialValue.nextLegendNumber ?? 1)
      : diagramSignature([], 1)
    const stored = draftKey ? loadDiagramFileFromStorage(draftKey) : null

    if (initialValue) {
      const canRecoverDraft = canRecoverDiagramDraft(
        stored,
        initialValue.elements,
        initialValue.nextLegendNumber ?? 1,
        persistedRevision ?? null,
      )
      seededElements = canRecoverDraft && stored ? stored.elements : initialValue.elements
      seededLegendNumber = canRecoverDraft && stored
        ? stored.nextLegendNumber
        : (initialValue.nextLegendNumber ?? 1)
      seededCalibration = canRecoverDraft && stored
        ? stored.calibration
        : (initialValue.calibration ?? null)
    } else if (stored) {
      seededElements = stored.elements
      seededLegendNumber = stored.nextLegendNumber
      seededCalibration = stored.calibration
    }
    if (draftKey) seededCalibration ??= loadCalibrationFromStorage(draftKey)

    resetHistory(seededElements)
    setNextLegendNumber(seededLegendNumber)
    setCalibration(seededCalibration)
    baselineSignatureRef.current = diagramSignature(seededElements, seededLegendNumber)
    baseSignatureRef.current = backendSignature
    baseRevisionRef.current = persistedRevision ?? null
    suppressedSignatureRef.current = null
    hydratedInitialValueRef.current = initialValue
    hydratedRef.current = true
  }, [draftKey, initialValue, loading, persistedRevision, resetHistory])

  useEffect(() => {
    if (persistedSignature) baseSignatureRef.current = persistedSignature
    baseRevisionRef.current = persistedRevision ?? null
  }, [persistedRevision, persistedSignature])

  useEffect(() => {
    if (!hydratedRef.current) return
    if (!draftKey) return
    saveCalibrationToStorage(calibration, draftKey)
  }, [calibration, draftKey])

  useEffect(() => {
    if (!hydratedRef.current) return
    if (!draftKey) return
    if (typeof window === 'undefined') return
    const signature = diagramSignature(elements, nextLegendNumberRef.current)
    if (signature === baselineSignatureRef.current || signature === suppressedSignatureRef.current) return
    const handle = window.setTimeout(
      () => saveDiagramToStorage(
        elements,
        nextLegendNumberRef.current,
        draftKey,
        baseSignatureRef.current,
        baseRevisionRef.current,
      ),
      400,
    )
    return () => window.clearTimeout(handle)
  }, [draftKey, elements])

  useEffect(() => {
    if (!hydratedRef.current) return
    const signature = diagramSignature(elements, nextLegendNumberRef.current)
    if (signature === baselineSignatureRef.current || signature === suppressedSignatureRef.current) return
    const handle = window.setTimeout(() => {
      onChangeRef?.current?.({
        schemaVersion: DIAGRAM_SCHEMA_VERSION,
        savedAt: new Date().toISOString(),
        elements,
        calibration: calibration ?? null,
        nextLegendNumber: nextLegendNumberRef.current,
      })
      baselineSignatureRef.current = signature
    }, 400)
    return () => window.clearTimeout(handle)
    // calibration intentionally omitted — only fire when elements change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements, onChangeRef])

  const handleSelect = useCallback((id: string, additive: boolean) => {
    setSelectedIds((current) => {
      if (additive) {
        return current.includes(id) ? current.filter((existing) => existing !== id) : [...current, id]
      }
      if (current.length === 1 && current[0] === id) return current
      return [id]
    })
  }, [])

  const addGear = useCallback((gearType: GearType, subtypeId?: string) => {
    let width = DEFAULT_GEAR_SIZE
    let height = DEFAULT_GEAR_SIZE
    if (gearType === 're-anchor') { width = 140; height = DEFAULT_GEAR_SIZE }
    if (gearType === 'i-beam-h') { width = 200; height = 50 }
    if (gearType === 'i-beam-v') { width = 50; height = 200 }
    if (gearType === 'stretcher') { width = 130; height = 60 }
    const stageH = containerSize.height - MENUBAR_HEIGHT - ribbonHeight
    const center = viewportCenterWorld(viewport, containerSize.width, stageH)
    const cascade = (presentRef.current.length % 6) * 22
    const worldX = center.x - width / 2 + cascade
    const worldY = center.y - height / 2 + cascade
    const legendNum = nextLegendNumberRef.current
    setNextLegendNumber(legendNum + 1)
    const element: GearElement = {
      id: makeId(gearType),
      kind: 'gear',
      gearType,
      x: worldX,
      y: worldY,
      width,
      height,
      rotation: 0,
      legendNumber: legendNum,
      ...(subtypeId ? { subtype: subtypeId } : {}),
    }
    history.set((current) => [...current, element])
    setSelectedIds([element.id])
    setTool('select')
  }, [history, viewport, containerSize, ribbonHeight])

  const placeTemplate = useCallback((template: TemplateDefinition) => {
    const stageH = containerSize.height - MENUBAR_HEIGHT - ribbonHeight
    const center = viewportCenterWorld(viewport, containerSize.width, stageH)
    const cascade = (presentRef.current.length % 6) * 22

    // Build at origin so we can measure the raw bounding box.
    const rawElements = template.build(0, 0)

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    for (const el of rawElements) {
      if (el.kind === 'gear') {
        minX = Math.min(minX, el.x)
        minY = Math.min(minY, el.y)
        maxX = Math.max(maxX, el.x + el.width)
        maxY = Math.max(maxY, el.y + el.height)
      } else if (el.kind === 'rope') {
        if (el.start.mode === 'free') {
          minX = Math.min(minX, el.start.x)
          minY = Math.min(minY, el.start.y)
          maxX = Math.max(maxX, el.start.x)
          maxY = Math.max(maxY, el.start.y)
        }
        if (el.end.mode === 'free') {
          minX = Math.min(minX, el.end.x)
          minY = Math.min(minY, el.end.y)
          maxX = Math.max(maxX, el.end.x)
          maxY = Math.max(maxY, el.end.y)
        }
      }
    }

    const rawW = maxX - minX
    const rawH = maxY - minY
    const s = Math.min(1, MAX_TEMPLATE_EXTENT / Math.max(rawW, rawH, 1))
    const scaledW = rawW * s
    const scaledH = rawH * s
    const ox = center.x + cascade - scaledW / 2
    const oy = center.y + cascade - scaledH / 2

    let legendCounter = nextLegendNumberRef.current
    const newElements: DrawingElement[] = rawElements.map((el) => {
      if (el.kind === 'gear') {
        const num = legendCounter++
        return {
          ...el,
          x: ox + (el.x - minX) * s,
          y: oy + (el.y - minY) * s,
          width: el.width * s,
          height: el.height * s,
          legendNumber: num,
        }
      }
      if (el.kind === 'rope') {
        const scaleEnd = (end: RopeEnd): RopeEnd =>
          end.mode === 'free'
            ? { ...end, x: ox + (end.x - minX) * s, y: oy + (end.y - minY) * s }
            : end
        return { ...el, start: scaleEnd(el.start), end: scaleEnd(el.end), diameter: Math.round(el.diameter * s * 2) / 2 }
      }
      return el
    })
    setNextLegendNumber(legendCounter)

    history.set((current) => [...current, ...newElements])
    setSelectedIds(newElements.map((e) => e.id))
    setTool('select')
  }, [history, viewport, containerSize, ribbonHeight])

  const handleStageMouseDown = useCallback((event: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    const stage = stageRef.current
    if (!stage) return

    // Two-finger touch → start a pinch-zoom / pan gesture and abandon any draft.
    const touchEvt = event.evt as TouchEvent
    if (typeof touchEvt.touches !== 'undefined' && touchEvt.touches.length >= 2) {
      const gesture = getTwoTouchGesture(stageRef, touchEvt.touches)
      if (gesture) {
        touchEvt.preventDefault()
        pinchRef.current = { distance: gesture.distance, center: gesture.center, viewport }
        setDraftRope(null)
        setDraftShape(null)
        setDraftFreedraw(null)
        setDraftScale(null)
        setMarquee(null)
        setSnapCandidate(null)
        return
      }
    }

    const clickedStage = event.target === stage || event.target.name() === 'canvas-background'
    const pointer = getPointer(stageRef, viewport)
    if (!pointer) return

    const native = event.evt as MouseEvent
    if (native.button === 1 || native.altKey) {
      setIsPanning(true)
      setPanStart({
        pointer: { x: native instanceof MouseEvent ? native.clientX : 0, y: native instanceof MouseEvent ? native.clientY : 0 },
        viewport,
      })
      return
    }

    if (scaleModeRef.current) {
      setDraftScale({ start: pointer, current: pointer })
      return
    }

    if (tool === 'select') {
      if (!clickedStage) return
      const additive = Boolean(native.shiftKey || native.ctrlKey || native.metaKey)
      setMarquee({
        start: pointer,
        current: pointer,
        additive,
        baseSelection: additive ? selectedIdsRef.current : [],
      })
      if (!additive) setSelectedIds([])
      return
    }

    if (clickedStage) setSelectedIds([])

    if (tool === 'freedraw') {
      setDraftFreedraw({ id: makeId('freedraw'), points: [pointer.x, pointer.y] })
      return
    }

    if (tool === 'rope') {
      const candidate = findNearestSnap(pointer, gears)
      const start = candidate?.point ?? pointer
      setDraftRope({ start, current: start, startSnap: candidate, currentSnap: candidate })
      setSnapCandidate(candidate)
      return
    }

    setDraftShape({ id: makeId('shape'), tool, start: pointer, current: pointer })
  }, [gears, tool, viewport])

  const handleStageMouseMove = useCallback((event: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    const touchEvt = event.evt as TouchEvent
    if (pinchRef.current && typeof touchEvt.touches !== 'undefined') {
      const gesture = getTwoTouchGesture(stageRef, touchEvt.touches)
      if (gesture) {
        touchEvt.preventDefault()
        const start = pinchRef.current
        const ratio = gesture.distance > 0 && start.distance > 0 ? gesture.distance / start.distance : 1
        const nextScale = Math.max(0.35, Math.min(2.4, start.viewport.scale * ratio))
        // World point under the gesture's original midpoint; keep it pinned to
        // the current midpoint so the diagram zooms and pans under the fingers.
        const worldX = (start.center.x - start.viewport.x) / start.viewport.scale
        const worldY = (start.center.y - start.viewport.y) / start.viewport.scale
        setViewport({
          scale: nextScale,
          x: gesture.center.x - worldX * nextScale,
          y: gesture.center.y - worldY * nextScale,
        })
      }
      return
    }

    if (isPanning && panStart && event.evt instanceof MouseEvent) {
      setViewport({
        ...panStart.viewport,
        x: panStart.viewport.x + event.evt.clientX - panStart.pointer.x,
        y: panStart.viewport.y + event.evt.clientY - panStart.pointer.y,
      })
      return
    }

    const pointer = getPointer(stageRef, viewport)
    if (!pointer) return

    if (draftScaleRef.current) {
      setDraftScale({ start: draftScaleRef.current.start, current: pointer })
      return
    }

    if (draftFreedraw) {
      setDraftFreedraw((prev) => prev ? { ...prev, points: [...prev.points, pointer.x, pointer.y] } : null)
      return
    }

    if (draftRope) {
      const candidate = findNearestSnap(pointer, gears)
      setDraftRope({
        ...draftRope,
        current: candidate?.point ?? pointer,
        currentSnap: candidate,
      })
      setSnapCandidate(candidate)
      return
    }

    if (draftShape) {
      setDraftShape({ ...draftShape, current: pointer })
      return
    }

    if (marquee) {
      setMarquee({ ...marquee, current: pointer })
    }
  }, [draftFreedraw, draftRope, draftShape, gears, isPanning, marquee, panStart, viewport])

  const handleStageMouseUp = useCallback(() => {
    // Safety: clean up leaked ropeHandleDrag state if a midpoint handle
    // unmounted mid-drag (onDragEnd never fired on the destroyed node).
    setRopeHandleDrag(null)
    setSnapCandidate(null)

    if (pinchRef.current) {
      pinchRef.current = null
      return
    }

    if (isPanning) {
      setIsPanning(false)
      setPanStart(null)
      return
    }

    if (draftScaleRef.current) {
      const { start, current } = draftScaleRef.current
      setDraftScale(null)
      setScaleMode(false)
      const worldDist = Math.hypot(current.x - start.x, current.y - start.y)
      if (worldDist >= 4) {
        const input = window.prompt('Real-world length of this line, in metres:', '1')
        if (input != null) {
          const meters = Number.parseFloat(input)
          if (Number.isFinite(meters) && meters > 0) {
            setCalibration({ pixelsPerMeter: worldDist / meters })
          } else {
            window.alert('Enter a positive number of metres.')
          }
        }
      }
      return
    }

    if (draftFreedraw) {
      if (draftFreedraw.points.length >= 4) {
        const element: FreedrawElement = {
          id: draftFreedraw.id,
          kind: 'freedraw',
          points: draftFreedraw.points,
          stroke: '#111827',
          strokeWidth: 3,
        }
        history.set((current) => [...current, element])
        setSelectedIds([element.id])
      }
      setDraftFreedraw(null)
      return
    }

    if (draftRope) {
      const distance = Math.hypot(draftRope.current.x - draftRope.start.x, draftRope.current.y - draftRope.start.y)
      if (distance > 8) {
        // Re-evaluate snaps at release with the forgiving drop radius so an end
        // released reasonably near a gear attaches, rather than dropping `free`
        // just short of it.
        const startSnap = findNearestSnap(draftRope.start, gears, SNAP_DROP_DISTANCE) ?? draftRope.startSnap
        const endSnap = findNearestSnap(draftRope.current, gears, SNAP_DROP_DISTANCE)
        const rope: RopeElement = {
          id: makeId('rope'),
          kind: 'rope',
          start: startSnap
            ? { mode: 'attached', elementId: startSnap.elementId, snapPointId: startSnap.snapPointId }
            : { mode: 'free', x: draftRope.start.x, y: draftRope.start.y, fallsToGround: true },
          end: snapCandidateToRopeEnd(endSnap, draftRope.current),
          contacts: [],
          color: getRopeRoleStyle(ropeRole)?.color ?? '#111827',
          diameter: 5,
          role: ropeRole,
        }
        history.set((current) => [...current, rope])
        setSelectedIds([rope.id])
      }
      setDraftRope(null)
      setSnapCandidate(null)
      setTool('select')
      return
    }

    if (draftShape) {
      const distance = Math.hypot(draftShape.current.x - draftShape.start.x, draftShape.current.y - draftShape.start.y)
      if (distance > 6 || draftShape.tool === 'text') {
        const shape = normalizeShapeDraft(draftShape, draftShape.id)
        history.set((current) => [...current, shape])
        setSelectedIds([shape.id])
        if (draftShape.tool === 'text') {
          setEditingText({ id: shape.id, value: shape.text ?? '' })
        }
      }
      setDraftShape(null)
      setTool('select')
      return
    }

    if (marquee) {
      const box = normalizeMarquee(marquee.start, marquee.current)
      const width = box.maxX - box.minX
      const height = box.maxY - box.minY
      if (width >= 3 || height >= 3) {
        const hits = computeMarqueeSelection(presentRef.current, gears, box)
        setSelectedIds(marquee.additive ? uniqueIds([...marquee.baseSelection, ...hits]) : hits)
      }
      setMarquee(null)
    }
  }, [draftFreedraw, draftRope, draftShape, gears, history, isPanning, marquee, ropeRole])

  const handleWheel = useCallback((event: Konva.KonvaEventObject<WheelEvent>) => {
    event.evt.preventDefault()
    const stage = stageRef.current
    const pointer = stage?.getPointerPosition()
    if (!pointer) return
    const scaleBy = 1.06
    const oldScale = viewport.scale
    const nextScale = event.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy
    const clampedScale = Math.max(0.35, Math.min(2.4, nextScale))
    const mousePointTo = {
      x: (pointer.x - viewport.x) / oldScale,
      y: (pointer.y - viewport.y) / oldScale,
    }
    setViewport({
      scale: clampedScale,
      x: pointer.x - mousePointTo.x * clampedScale,
      y: pointer.y - mousePointTo.y * clampedScale,
    })
  }, [viewport])

  const handleElementDragStart = useCallback((id: string) => {
    let activeSelection = selectedIdsRef.current
    if (!activeSelection.includes(id)) {
      activeSelection = [id]
      setSelectedIds(activeSelection)
    }
    dragSnapshotRef.current = presentRef.current
    const origins = new Map<string, Point>()
    const allGears = getGearElements(presentRef.current)
    for (const element of presentRef.current) {
      if ((element.kind === 'gear' || element.kind === 'shape') && activeSelection.includes(element.id)) {
        origins.set(element.id, { x: element.x, y: element.y })
      }
    }
    if (!origins.has(id)) {
      const dragged = presentRef.current.find((el) => el.id === id)
      if (dragged && (dragged.kind === 'gear' || dragged.kind === 'shape')) {
        origins.set(id, { x: dragged.x, y: dragged.y })
      }
    }

    // Include descendant gears (children attached via parentElementId) so they
    // move together with the dragged parent.
    for (const originId of [...origins.keys()]) {
      const descendants = getDescendantGears(originId, allGears)
      for (const desc of descendants) {
        if (!origins.has(desc.id)) {
          origins.set(desc.id, { x: desc.x, y: desc.y })
        }
      }
    }

    dragOriginsRef.current = origins

    // A rope's free end should translate with the gear its other end is
    // attached to, so a hanging line follows its anchor instead of pivoting.
    const ropeFollow = new Map<string, { start?: Point; end?: Point }>()
    for (const element of presentRef.current) {
      if (element.kind !== 'rope') continue
      const entry: { start?: Point; end?: Point } = {}
      if (
        element.start.mode === 'free' &&
        element.end.mode === 'attached' &&
        origins.has(element.end.elementId)
      ) {
        entry.start = { x: element.start.x, y: element.start.y }
      }
      if (
        element.end.mode === 'free' &&
        element.start.mode === 'attached' &&
        origins.has(element.start.elementId)
      ) {
        entry.end = { x: element.end.x, y: element.end.y }
      }
      if (entry.start || entry.end) ropeFollow.set(element.id, entry)
    }
    ropeFollowOriginsRef.current = ropeFollow
  }, [])

  const applyElementDragDelta = useCallback((id: string, x: number, y: number) => {
    const origin = dragOriginsRef.current.get(id)
    if (!origin) {
      history.replace((current) => current.map((element) => {
        if (element.id !== id) return element
        if (element.kind === 'gear' || element.kind === 'shape') return { ...element, x, y }
        return element
      }))
      return
    }
    const dx = x - origin.x
    const dy = y - origin.y
    history.replace((current) => current.map((element) => {
      if (element.kind === 'gear' || element.kind === 'shape') {
        const elementOrigin = dragOriginsRef.current.get(element.id)
        if (!elementOrigin) return element
        return { ...element, x: elementOrigin.x + dx, y: elementOrigin.y + dy }
      }
      if (element.kind === 'rope') {
        const follow = ropeFollowOriginsRef.current.get(element.id)
        if (!follow) return element
        let next = element
        if (follow.start && next.start.mode === 'free') {
          next = { ...next, start: { ...next.start, x: follow.start.x + dx, y: follow.start.y + dy } }
        }
        if (follow.end && next.end.mode === 'free') {
          next = { ...next, end: { ...next.end, x: follow.end.x + dx, y: follow.end.y + dy } }
        }
        return next
      }
      return element
    }))
  }, [history])

  const handleElementDragMove = useCallback((id: string, x: number, y: number) => {
    applyElementDragDelta(id, x, y)

    // Gear-to-gear snap highlight: show the green indicator when a dragged
    // gear's connectable snap point is near another gear's snap point.
    const current = presentRef.current
    const draggedElement = current.find((el) => el.id === id)
    if (draggedElement && draggedElement.kind === 'gear') {
      const allGears = getGearElements(current)
      const selected = new Set(selectedIdsRef.current)
      const match = findNearestGearSnap(draggedElement, allGears, SNAP_DISTANCE, selected)
      setSnapCandidate(match?.target ?? null)
    }
  }, [applyElementDragDelta])

  const handleElementDragEnd = useCallback((id: string, x: number, y: number) => {
    applyElementDragDelta(id, x, y)

    // Auto-snap: if a gear was dropped near a rope, attach it as a contact
    const current = presentRef.current
    const draggedElement = current.find((el) => el.id === id)
    if (draggedElement && draggedElement.kind === 'gear') {
      const snapPoints = getGearSnapPoints(draggedElement)
      const allGears = getGearElements(current)
      const ropes = current.filter((el): el is RopeElement => el.kind === 'rope')

      const matchedRopeIds = new Set<string>()
      let snappedToRope = false
      for (const snap of snapPoints) {
        if (snappedToRope) break
        if (snap.role === 'generic') continue
        for (const rope of ropes) {
          if (matchedRopeIds.has(rope.id)) continue
          // Skip if gear is already a contact on this rope
          if (rope.contacts.some((c) => c.elementId === id)) continue
          // Skip if gear is already an endpoint on this rope
          if ((rope.start.mode === 'attached' && rope.start.elementId === id) ||
              (rope.end.mode === 'attached' && rope.end.elementId === id)) continue

          const ropePoints = getRopeControlPoints(rope, allGears)
          const segIdx = findBestSegmentIndex(ropePoints, snap.world)
          if (segIdx < 0) continue

          // Check distance from snap point to the nearest rope segment
          const a = ropePoints[segIdx]
          const b = ropePoints[segIdx + 1]
          if (!a || !b) continue
          const dx = b.x - a.x
          const dy = b.y - a.y
          const len2 = dx * dx + dy * dy
          const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((snap.world.x - a.x) * dx + (snap.world.y - a.y) * dy) / len2))
          const cx = a.x + t * dx
          const cy = a.y + t * dy
          const dist = Math.hypot(snap.world.x - cx, snap.world.y - cy)

          if (dist <= 40) {
            const candidate: SnapCandidate = {
              elementId: id,
              snapPointId: snap.id,
              point: snap.world,
              role: snap.role,
              distance: dist,
            }
            history.replace((elements) => elements.map((el) => {
              if (el.kind !== 'rope' || el.id !== rope.id) return el
              return insertRopeContactAt(el, segIdx, candidate)
            }))
            matchedRopeIds.add(rope.id)
            snappedToRope = true
            break
          }
        }
      }

      // If a child gear snapped to a rope, detach it from its parent gear so it
      // doesn't end up dual-attached (rope contact + gear child).
      if (snappedToRope && draggedElement.parentElementId) {
        history.replace((elements) => elements.map((el) => {
          if (el.kind !== 'gear' || el.id !== id) return el
          const { parentElementId: _p, parentSnapPointId: _s, ...rest } = el
          return rest as GearElement
        }))
      }

      // Gear-to-gear chaining: if the gear wasn't snapped to a rope, try
      // snapping it to another gear's snap point to form a parent attachment.
      if (!snappedToRope) {
        const descendantIds = new Set(getDescendantGears(id, allGears).map((g) => g.id))
        descendantIds.add(id)
        // Use the center of the dragged gear as the probe point
        const gearCenter: Point = {
          x: draggedElement.x + draggedElement.width / 2,
          y: draggedElement.y + draggedElement.height / 2,
        }
        const gearSnap = findNearestParentGearSnap(gearCenter, allGears, descendantIds)
        if (gearSnap) {
          // Attach: set parentElementId/parentSnapPointId and snap position
          const childPos = computeChildPosition(draggedElement, gearSnap.gear, gearSnap.snapPointId)
          const snapDx = childPos.x - draggedElement.x
          const snapDy = childPos.y - draggedElement.y
          const descendants = getDescendantGears(id, allGears)
          const descIds = new Set(descendants.map((g) => g.id))
          history.replace((elements) => elements.map((el) => {
            if (el.kind !== 'gear') return el
            if (el.id === id) {
              return {
                ...el,
                x: childPos.x,
                y: childPos.y,
                parentElementId: gearSnap.gear.id,
                parentSnapPointId: gearSnap.snapPointId,
              }
            }
            if (descIds.has(el.id)) {
              return { ...el, x: el.x + snapDx, y: el.y + snapDy }
            }
            return el
          }))
        } else if (draggedElement.parentElementId) {
          // Detach: gear was dragged away from its parent
          history.replace((elements) => elements.map((el) => {
            if (el.kind !== 'gear' || el.id !== id) return el
            const { parentElementId: _p, parentSnapPointId: _s, ...rest } = el
            return rest as GearElement
          }))
        }
      }

      // Gear-to-gear auto-snap: when a gear is dropped near another gear's
      // connectable snap point (e.g. pulley becket onto load attachment),
      // create a short rope connecting them.
      if (!snappedToRope) {
        const selected = new Set(selectedIdsRef.current)
        const gearMatch = findNearestGearSnap(draggedElement, allGears, SNAP_DROP_DISTANCE, selected)
        if (gearMatch) {
          // Skip if these two gears are already connected by a rope endpoint
          const targetId = gearMatch.target.elementId
          const alreadyConnected = ropes.some((rope) => {
            const sId = rope.start.mode === 'attached' ? rope.start.elementId : null
            const eId = rope.end.mode === 'attached' ? rope.end.elementId : null
            return (sId === id && eId === targetId) || (sId === targetId && eId === id)
          })
          if (!alreadyConnected) {
            const roleRef = ropeRoleRef.current
            const newRope: RopeElement = {
              id: makeId('rope'),
              kind: 'rope',
              start: { mode: 'attached', elementId: id, snapPointId: gearMatch.sourceSnapId },
              end: { mode: 'attached', elementId: targetId, snapPointId: gearMatch.target.snapPointId },
              contacts: [],
              color: getRopeRoleStyle(roleRef)?.color ?? '#111827',
              diameter: 5,
              role: roleRef,
            }
            history.replace((elements) => [...elements, newRope])
          }
        }
      }
    }

    setSnapCandidate(null)

    if (dragSnapshotRef.current) {
      history.commit(dragSnapshotRef.current)
      dragSnapshotRef.current = null
    }
    dragOriginsRef.current = new Map()
    ropeFollowOriginsRef.current = new Map()
  }, [applyElementDragDelta, history])

  // ── Endpoint handles for line / arrow ──────────────────────────────
  const handleEndpointDragStart = useCallback((_id: string) => {
    endpointSnapshotRef.current = presentRef.current
  }, [])

  const handleEndpointDrag = useCallback((id: string, end: 'start' | 'end', worldX: number, worldY: number) => {
    history.replace((current) => current.map((el) => {
      if (el.id !== id || el.kind !== 'shape') return el
      if (end === 'end') {
        // Keep start point fixed, move end point
        return { ...el, width: roundTo(worldX - el.x, 2), height: roundTo(worldY - el.y, 2) }
      }
      // Move start point, keep end point fixed
      const endX = el.x + el.width
      const endY = el.y + el.height
      return { ...el, x: roundTo(worldX, 2), y: roundTo(worldY, 2), width: roundTo(endX - worldX, 2), height: roundTo(endY - worldY, 2) }
    }))
  }, [history])

  const handleEndpointDragEnd = useCallback(() => {
    if (endpointSnapshotRef.current) {
      history.commit(endpointSnapshotRef.current)
      endpointSnapshotRef.current = null
    }
  }, [history])

  const handleRopeHandleDragStart = useCallback((_ref: RopeHandleRef) => {
    // No-op: ropeHandleDrag drives the visual override, and history.present stays
    // untouched until dragEnd, so we get exactly one history entry per drag.
  }, [])

  const handleRopeHandleDragMove = useCallback((ref: RopeHandleRef, x: number, y: number): Point => {
    const point = { x, y }
    const candidate = findNearestSnap(point, getGearElements(presentRef.current))
    const effective = candidate ? candidate.point : point
    setRopeHandleDrag({ ropeId: ref.ropeId, ref, position: effective, candidate })
    setSnapCandidate(candidate)
    return effective
  }, [])

  const handleRopeHandleDragEnd = useCallback((ref: RopeHandleRef, x: number, y: number) => {
    const point = { x, y }
    const gearsAtEnd = getGearElements(presentRef.current)
    // Forgiving drop radius on release so an end let go near a gear attaches.
    const candidate = findNearestSnap(point, gearsAtEnd, SNAP_DROP_DISTANCE)

    history.set((current) => {
      let mutated = false
      const next = current.map((element) => {
        if (element.kind !== 'rope' || element.id !== ref.ropeId) return element
        if (ref.kind === 'endpoint') {
          mutated = true
          return setRopeEndpoint(element, ref.end, candidate, point)
        }
        if (ref.kind === 'contact') {
          mutated = true
          return candidate
            ? setRopeContact(element, ref.index, candidate)
            : removeRopeContact(element, ref.index)
        }
        // midpoint: only insert when a snap is acquired
        if (candidate) {
          const controlPoints = getRopeControlPoints(element, gearsAtEnd)
          const segmentIndex = findBestSegmentIndex(controlPoints, point)
          mutated = true
          return insertRopeContactAt(element, segmentIndex, candidate)
        }
        return element
      })
      return mutated ? next : current
    })

    setRopeHandleDrag(null)
    setSnapCandidate(null)
  }, [history])

  const handleTransform = useCallback(() => {
    const transformer = transformerRef.current
    if (!transformer) return

    let hasSpanUpdate = false

    for (const node of transformer.nodes()) {
      const id = node.id()
      const element = presentRef.current.find((el) => el.id === id)
      if (!element) continue

      // Span gear: bake width from scaleX live, reset scale so end glyphs
      // stay fixed-size during the drag (not just on release).
      if (element.kind === 'gear' && SPAN_GEAR_TYPES.has(element.gearType)) {
        const sx = Math.abs(node.scaleX())
        const sy = Math.abs(node.scaleY())
        if (Math.abs(sx - 1) > 0.001 || Math.abs(sy - 1) > 0.001) {
          const newWidth = Math.max(40, node.width() * sx)
          const newHeight = Math.max(20, node.height() * sy)
          node.scaleX(1)
          node.scaleY(1)
          node.width(newWidth)
          node.height(newHeight)
          node.offsetX(newWidth / 2)
          node.offsetY(newHeight / 2)
          hasSpanUpdate = true
        }
        continue
      }

      // Non-span gear handling
      if (element.kind === 'gear') {
        if (transformer.nodes().length > 1) {
          // Multi-select: keep icon size fixed, only reposition
          node.scaleX(1)
          node.scaleY(1)
        } else if (element.gearType !== 'i-beam-h' && element.gearType !== 'i-beam-v') {
          // Single-select non-I-beam: enforce uniform scale so icon doesn't distort
          const avgScale = (Math.abs(node.scaleX()) + Math.abs(node.scaleY())) / 2
          node.scaleX(avgScale)
          node.scaleY(avgScale)
        }
        // Single I-beam: transformerConfig already constrains to one axis
        continue
      }

      if (element.kind !== 'shape') continue
      if (element.shapeType !== 'line' && element.shapeType !== 'arrow' && element.shapeType !== 'i-beam') continue
      const origLen = Math.hypot(element.width, element.height)
      if (origLen < 0.01) continue
      const sx = node.scaleX()
      const sy = node.scaleY()
      const newLen = Math.hypot(element.width * sx, element.height * sy)
      const uniformScale = newLen / origLen
      node.scaleX(uniformScale)
      node.scaleY(uniformScale)
    }

    // Live-update span gear dimensions so ReAnchorSpanIcon re-renders with
    // correct fixed-glyph proportions during drag.
    if (hasSpanUpdate) {
      history.replace((current) => current.map((el) => {
        if (el.kind !== 'gear' || !SPAN_GEAR_TYPES.has(el.gearType)) return el
        const node = nodeRefs.current.get(el.id)
        if (!node) return el
        const nw = node.width()
        const nh = node.height()
        if (nw === el.width && nh === el.height) return el
        return {
          ...el,
          width: roundTo(nw, 2),
          height: roundTo(nh, 2),
          x: roundTo(node.x() - nw / 2, 2),
          y: roundTo(node.y() - nh / 2, 2),
        }
      }))
    }
  }, [history])

  const handleTransformStart = useCallback(() => {
    transformSnapshotRef.current = presentRef.current
  }, [])

  const handleTransformEnd = useCallback(() => {
    const transformer = transformerRef.current
    if (!transformer) return
    const transformedNodes = transformer.nodes()
    if (transformedNodes.length === 0) return

    const transformResults = new Map<string, { x: number; y: number; scaleX: number; scaleY: number; rotation: number }>()
    for (const node of transformedNodes) {
      const id = node.id()
      if (!id) continue
      transformResults.set(id, {
        x: node.x(),
        y: node.y(),
        scaleX: node.scaleX(),
        scaleY: node.scaleY(),
        rotation: node.rotation(),
      })
      node.scaleX(1)
      node.scaleY(1)
    }

    history.replace((current) => current.map((element) => {
      const result = transformResults.get(element.id)
      if (!result) return element
      if (element.kind === 'gear') {
        // Multi-select: non-span gear keeps its icon size, only repositions.
        // This prevents template group resizes from ballooning plain anchors.
        if (transformedNodes.length > 1 && !SPAN_GEAR_TYPES.has(element.gearType)) {
          return {
            ...element,
            x: roundTo(result.x - element.width / 2, 2),
            y: roundTo(result.y - element.height / 2, 2),
            rotation: roundTo(result.rotation, 2),
          }
        }
        if (element.gearType === 'i-beam-h') {
          const newWidth = Math.max(20, element.width * Math.abs(result.scaleX))
          return {
            ...element,
            x: roundTo(result.x - newWidth / 2, 2),
            y: roundTo(result.y - element.height / 2, 2),
            width: roundTo(newWidth, 2),
            rotation: roundTo(result.rotation, 2),
          }
        }
        if (element.gearType === 'i-beam-v') {
          const newHeight = Math.max(20, element.height * Math.abs(result.scaleY))
          return {
            ...element,
            x: roundTo(result.x - element.width / 2, 2),
            y: roundTo(result.y - newHeight / 2, 2),
            height: roundTo(newHeight, 2),
            rotation: roundTo(result.rotation, 2),
          }
        }
        let newWidth: number
        let newHeight: number
        if (SPAN_GEAR_TYPES.has(element.gearType)) {
          // Span gear: stretch width freely
          newWidth = Math.max(40, element.width * Math.abs(result.scaleX))
          newHeight = Math.max(20, element.height * Math.abs(result.scaleY))
        } else {
          // Single-select plain gear: uniform scale
          const scale = (Math.abs(result.scaleX) + Math.abs(result.scaleY)) / 2
          newWidth = Math.max(20, element.width * scale)
          newHeight = Math.max(20, element.height * scale)
        }
        return {
          ...element,
          x: roundTo(result.x - newWidth / 2, 2),
          y: roundTo(result.y - newHeight / 2, 2),
          width: roundTo(newWidth, 2),
          height: roundTo(newHeight, 2),
          rotation: roundTo(result.rotation, 2),
        }
      }
      if (element.kind === 'shape') {
        if (element.shapeType === 'line' || element.shapeType === 'arrow') {
          const origLen = Math.hypot(element.width, element.height)
          if (origLen < 0.01) {
            return { ...element, x: roundTo(result.x, 2), y: roundTo(result.y, 2), rotation: roundTo(result.rotation, 2) }
          }
          const localAngle = Math.atan2(element.height, element.width)
          const newLen = Math.hypot(element.width * result.scaleX, element.height * result.scaleY)
          const newW = newLen * Math.cos(localAngle)
          const newH = newLen * Math.sin(localAngle)
          return {
            ...element,
            x: roundTo(result.x - newW / 2, 2),
            y: roundTo(result.y - newH / 2, 2),
            width: roundTo(newW, 2),
            height: roundTo(newH, 2),
            rotation: roundTo(result.rotation, 2),
          }
        }
        if (element.shapeType === 'text') {
          const scale = Math.max(Math.abs(result.scaleX), Math.abs(result.scaleY))
          const newFontSize = Math.max(6, (element.fontSize ?? 20) * scale)
          const newWidth = Math.max(20, element.width * Math.abs(result.scaleX))
          const newHeight = Math.max(20, element.height * Math.abs(result.scaleY))
          return {
            ...element,
            x: roundTo(result.x - newWidth / 2, 2),
            y: roundTo(result.y - newHeight / 2, 2),
            width: roundTo(newWidth, 2),
            height: roundTo(newHeight, 2),
            fontSize: roundTo(newFontSize, 2),
            rotation: roundTo(result.rotation, 2),
          }
        }
        if (element.shapeType === 'i-beam') {
          const localAngle = Math.atan2(element.height, element.width)
          const oldLen = Math.hypot(element.width, element.height)
          const newLen = oldLen * Math.max(Math.abs(result.scaleX), Math.abs(result.scaleY))
          const newW = newLen * Math.cos(localAngle)
          const newH = newLen * Math.sin(localAngle)
          return {
            ...element,
            x: roundTo(result.x - newW / 2, 2),
            y: roundTo(result.y - newH / 2, 2),
            width: roundTo(newW, 2),
            height: roundTo(newH, 2),
            rotation: roundTo(result.rotation, 2),
          }
        }
        const newWidth = element.width * result.scaleX
        const newHeight = element.height * result.scaleY
        return {
          ...element,
          x: roundTo(result.x - newWidth / 2, 2),
          y: roundTo(result.y - newHeight / 2, 2),
          width: roundTo(newWidth, 2),
          height: roundTo(newHeight, 2),
          rotation: roundTo(result.rotation, 2),
        }
      }
      return element
    }))

    if (transformSnapshotRef.current) {
      history.commit(transformSnapshotRef.current)
      transformSnapshotRef.current = null
    }
  }, [history])

  const updateElementById = useCallback((id: string, updater: (element: DrawingElement) => DrawingElement) => {
    history.set((current) => current.map((element) => element.id === id ? updater(element) : element))
  }, [history])

  const beginEditText = useCallback((id: string) => {
    const target = presentRef.current.find((element) => element.id === id)
    if (!target || target.kind !== 'shape' || target.shapeType !== 'text') return
    setSelectedIds([id])
    setEditingText({ id, value: target.text ?? '' })
  }, [])

  const commitEditText = useCallback(() => {
    setEditingText((current) => {
      if (!current) return current
      history.set((elements) => elements.map((element) => {
        if (element.id !== current.id) return element
        if (element.kind !== 'shape' || element.shapeType !== 'text') return element
        return { ...element, text: current.value }
      }))
      return null
    })
  }, [history])

  const cancelEditText = useCallback(() => {
    setEditingText(null)
  }, [])

  const deleteSelected = useCallback(() => {
    if (selectedIds.length === 0) return
    if (selectedIds.length === presentRef.current.length) {
      setDeleteAllAction('selection')
      return
    }
    history.set((current) => deleteElementsByIds(current, selectedIds))
    setSelectedIds([])
  }, [history, selectedIds])

  const selectAll = useCallback(() => {
    setSelectedIds(presentRef.current.map((element) => element.id))
  }, [])

  const clearCanvas = useCallback(() => {
    suppressedSignatureRef.current = diagramSignature([], 1)
    history.set([])
    setSelectedIds([])
    setDraftRope(null)
    setDraftShape(null)
    setMarquee(null)
    setSnapCandidate(null)
    setShowHelp(false)
    setDraftScale(null)
    setScaleMode(false)
    setNextLegendNumber(1)
    if (draftKey) clearDiagramStorage(draftKey)
    setDeleteAllAction(null)
  }, [draftKey, history])

  const confirmDeleteAll = useCallback(() => {
    clearCanvas()
  }, [clearCanvas])

  const getContentBox = useCallback((): BoundingBox | null => {
    return getContentBoundingBox(presentRef.current)
  }, [])

  const fitToContent = useCallback(() => {
    const box = getContentBox()
    if (!box || typeof window === 'undefined') return
    const barsH = MENUBAR_HEIGHT + ribbonHeight
    const pad = 80
    const vw = Math.max(containerSize.width, 100)
    const vh = Math.max(containerSize.height - barsH, 100)
    const contentW = Math.max(box.maxX - box.minX, 1)
    const contentH = Math.max(box.maxY - box.minY, 1)
    const fitScale = Math.min((vw - pad * 2) / contentW, (vh - pad * 2) / contentH, 2)
    const scale = Math.min(Math.max(fitScale, 0.1), 4)
    const cx = (box.minX + box.maxX) / 2
    const cy = (box.minY + box.maxY) / 2
    setViewport({ x: vw / 2 - cx * scale, y: vh / 2 - cy * scale, scale })
  }, [getContentBox, containerSize, ribbonHeight])

  const captureDiagramPng = useCallback((): string | null => {
    const stage = stageRef.current
    const contentGroup = contentGroupRef.current
    if (!stage || !contentGroup || presentRef.current.length === 0) return null
    const selectionSnapshot = selectedIds
    flushSync(() => setSelectedIds([]))
    transformerRef.current?.nodes([])
    const bgLayer = bgLayerRef.current
    const bgWasVisible = bgLayer?.visible() ?? true
    bgLayer?.visible(false)
    const overlayGroup = overlayGroupRef.current
    const overlaysWereVisible = overlayGroup?.visible() ?? true
    overlayGroup?.visible(false)
    const contentRect = contentGroup.getClientRect({ relativeTo: stage })
    const bounds = getExportBounds({
      minX: contentRect.x,
      minY: contentRect.y,
      maxX: contentRect.x + contentRect.width,
      maxY: contentRect.y + contentRect.height,
    }, 48)
    const prev = {
      x: stage.x(),
      y: stage.y(),
      scaleX: stage.scaleX(),
      scaleY: stage.scaleY(),
      width: stage.width(),
      height: stage.height(),
    }
    stage.scale({ x: 1, y: 1 })
    stage.position({ x: -bounds.x, y: -bounds.y })
    stage.size({ width: bounds.width, height: bounds.height })
    stage.draw()
    const dataUrl = stage.toDataURL({ mimeType: 'image/png', pixelRatio: 2 })
    bgLayer?.visible(bgWasVisible)
    overlayGroup?.visible(overlaysWereVisible)
    stage.size({ width: prev.width, height: prev.height })
    stage.scale({ x: prev.scaleX, y: prev.scaleY })
    stage.position({ x: prev.x, y: prev.y })
    stage.draw()
    flushSync(() => setSelectedIds(selectionSnapshot))
    return dataUrl
  }, [selectedIds])

  useEffect(() => {
    if (!exportPngRef) return
    ;(exportPngRef as React.MutableRefObject<(() => string | null) | null>).current = captureDiagramPng
    return () => {
      ;(exportPngRef as React.MutableRefObject<(() => string | null) | null>).current = null
    }
  }, [captureDiagramPng, exportPngRef])

  const handleExportPng = useCallback(() => {
    const dataUrl = captureDiagramPng()
    if (!dataUrl) {
      window.alert('Nothing to export yet — add some gear or ropes first.')
      return
    }
    const link = document.createElement('a')
    link.href = dataUrl
    link.download = timestampedName('rigging-diagram', 'png')
    document.body.appendChild(link)
    link.click()
    link.remove()
  }, [captureDiagramPng])

  const handleExportJson = useCallback(() => {
    downloadFile(timestampedName('rigging-diagram', 'json'), serializeDiagram(presentRef.current, calibration, nextLegendNumberRef.current), 'application/json')
  }, [calibration])

  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const toggleScaleMode = useCallback(() => {
    setScaleMode((on) => {
      const next = !on
      if (next) {
        setTool('select')
        setSelectedIds([])
        setDraftRope(null)
        setDraftShape(null)
        setDraftFreedraw(null)
        setMarquee(null)
      }
      setDraftScale(null)
      return next
    })
  }, [])

  const clearScale = useCallback(() => {
    setCalibration(null)
    setScaleMode(false)
    setDraftScale(null)
  }, [])

  const handleImportFile = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      const parsed = typeof reader.result === 'string' ? parseDiagramFile(reader.result) : null
      if (!parsed) {
        window.alert('Could not import: that file is not a valid rigging diagram JSON.')
        return
      }
      if (parsed.skipped > 0) {
        window.alert(
          `Imported ${parsed.elements.length} element(s) but skipped ${parsed.skipped} malformed element(s).`,
        )
      }
      history.reset(parsed.elements)
      setSelectedIds([])
      setCalibration(parsed.calibration)
      setNextLegendNumber(parsed.nextLegendNumber)
    }
    reader.readAsText(file)
  }, [history])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return

      const mod = event.ctrlKey || event.metaKey
      const key = event.key.toLowerCase()

      if (mod && !event.shiftKey && key === 'z') {
        event.preventDefault()
        history.undo()
        return
      }
      if ((mod && event.shiftKey && key === 'z') || (mod && key === 'y')) {
        event.preventDefault()
        history.redo()
        return
      }
      if (mod && key === 'a') {
        event.preventDefault()
        selectAll()
        return
      }
      if ((event.key === 'Delete' || event.key === 'Backspace') && selectedIds.length > 0) {
        event.preventDefault()
        deleteSelected()
        return
      }
      if (event.key === 'Escape') {
        setSelectedIds([])
        setEditingText(null)
        setScaleMode(false)
        setDraftScale(null)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [deleteSelected, history, selectAll, selectedIds.length])

  useEffect(() => {
    const transformer = transformerRef.current
    if (!transformer) return
    if (editingText) {
      transformer.nodes([])
      transformer.getLayer()?.batchDraw()
      return
    }
    const editableIds = selectedIds.filter((id) => {
      const element = presentRef.current.find((entry) => entry.id === id)
      if (!element || element.kind === 'rope' || element.kind === 'freedraw') return false
      // Exclude line/arrow from Transformer — they use custom endpoint handles
      if (element.kind === 'shape' && (element.shapeType === 'line' || element.shapeType === 'arrow')) return false
      return true
    })
    const nodes = editableIds
      .map((id) => nodeRefs.current.get(id))
      .filter((node): node is Konva.Node => Boolean(node))
    transformer.nodes(nodes)
    transformer.getLayer()?.batchDraw()
  }, [selectedIds, elements, editingText])

  const editingTextId = editingText?.id ?? null
  useEffect(() => {
    if (!editingTextId) return
    const handle = window.setTimeout(() => {
      textareaRef.current?.focus()
      textareaRef.current?.select()
    }, 10)
    return () => window.clearTimeout(handle)
  }, [editingTextId])

  const draftShapeElement = draftShape ? normalizeShapeDraft(draftShape, draftShape.id) : null
  const draftRopeElement: RopeElement | null = draftRope ? {
    id: 'draft-rope',
    kind: 'rope',
    start: draftRope.startSnap
      ? { mode: 'attached', elementId: draftRope.startSnap.elementId, snapPointId: draftRope.startSnap.snapPointId }
      : { mode: 'free', x: draftRope.start.x, y: draftRope.start.y, fallsToGround: false },
    end: { mode: 'free', x: draftRope.current.x, y: draftRope.current.y, fallsToGround: false },
    contacts: [],
    color: getRopeRoleStyle(ropeRole)?.color ?? '#0f172a',
    diameter: 4,
    role: ropeRole,
  } : null

  const marqueeBox = marquee ? normalizeMarquee(marquee.start, marquee.current) : null
  const isSelected = useCallback((id: string) => selectedIds.includes(id), [selectedIds])

  const selectedElements = useMemo(
    () => selectedIds.map((id) => elements.find((entry) => entry.id === id)).filter((entry): entry is DrawingElement => Boolean(entry)),
    [elements, selectedIds],
  )
  const singleSelection = selectedElements.length === 1 ? selectedElements[0] : null

  const transformerConfig = useMemo(() => {
    const editable = selectedElements.filter((entry) => entry.kind !== 'rope' && entry.kind !== 'freedraw')
    if (editable.length === 0) return { keepRatio: false, enabledAnchors: undefined as string[] | undefined }
    const soleIBeam = editable.length === 1 && editable[0].kind === 'gear'
      ? (editable[0].gearType === 'i-beam-h' ? 'i-beam-h' : editable[0].gearType === 'i-beam-v' ? 'i-beam-v' : null)
      : null
    if (soleIBeam) {
      const anchors = soleIBeam === 'i-beam-h'
        ? ['middle-left', 'middle-right']
        : ['top-center', 'bottom-center']
      return { keepRatio: false, enabledAnchors: anchors }
    }
    const hasIBeam = editable.some((entry) => entry.kind === 'gear' && (entry.gearType === 'i-beam-h' || entry.gearType === 'i-beam-v'))
    if (hasIBeam) return { keepRatio: false, enabledAnchors: undefined as string[] | undefined }
    const hasNonSpanGear = editable.some((entry) => entry.kind === 'gear' && !SPAN_GEAR_TYPES.has(entry.gearType))
    const hasSpanGear = editable.some((entry) => entry.kind === 'gear' && SPAN_GEAR_TYPES.has(entry.gearType))
    const hasText = editable.some((entry) => entry.kind === 'shape' && entry.shapeType === 'text')
    const hasLine = editable.some((entry) => entry.kind === 'shape' && (entry.shapeType === 'line' || entry.shapeType === 'arrow' || entry.shapeType === 'i-beam'))
    const hasOther = editable.some((entry) => entry.kind === 'shape' && entry.shapeType !== 'text' && entry.shapeType !== 'line' && entry.shapeType !== 'arrow' && entry.shapeType !== 'i-beam')
    // Span gear allows free width stretching; only lock ratio for non-span gear/text
    const keepRatio = (hasNonSpanGear || hasText) && !hasOther && !hasLine && !hasSpanGear
    const onlyLines = hasLine && !hasNonSpanGear && !hasSpanGear && !hasText && !hasOther
    const hasOnlyLineArrow = onlyLines && !editable.some((entry) => entry.kind === 'shape' && entry.shapeType === 'i-beam')
    const enabledAnchors: string[] | undefined = hasOnlyLineArrow
      ? []
      : onlyLines ? ['top-left', 'top-right', 'bottom-left', 'bottom-right'] : undefined
    return { keepRatio, enabledAnchors }
  }, [selectedElements])

  return (
    <div ref={containerRef} className="relative h-full w-full overflow-hidden bg-slate-100 text-slate-900">
      {/* ── BAR 1 — Menu bar ─────────────────────────────────── */}
      <div className="absolute left-0 top-0 z-30 flex h-8 w-full items-center border-b border-slate-200 bg-white/95 px-2 text-xs backdrop-blur" onClick={(e) => { if (e.target === e.currentTarget) setOpenMenu(null) }}>
        {(['File', 'Edit', 'Template', 'Scale'] as const).map((menu) => (
          <div key={menu} className="relative">
            <button
              type="button"
              onMouseDown={() => { setOpenMenu(openMenu === menu ? null : menu); setGearSubtypeMenu(null); setShowRopeRoleMenu(false) }}
              onMouseEnter={() => { if (openMenu) setOpenMenu(menu) }}
              className={`rounded px-3 py-1 font-medium transition ${openMenu === menu ? 'bg-blue-100 text-blue-700' : 'text-slate-600 hover:bg-slate-100'}`}
            >
              {menu}
            </button>
            {openMenu === menu && (
              <div className="absolute left-0 top-full z-50 mt-0.5 min-w-[200px] rounded-lg border border-slate-200 bg-white py-1 shadow-xl">
                {menu === 'File' && (
                  <>
                    <button type="button" onClick={() => { handleExportPng(); setOpenMenu(null) }} className="flex w-full px-4 py-1.5 text-left text-sm text-slate-700 hover:bg-blue-50">Export PNG</button>
                    <button type="button" onClick={() => { handleExportJson(); setOpenMenu(null) }} className="flex w-full px-4 py-1.5 text-left text-sm text-slate-700 hover:bg-blue-50">Export JSON</button>
                    <button type="button" onClick={() => { handleImportClick(); setOpenMenu(null) }} className="flex w-full px-4 py-1.5 text-left text-sm text-slate-700 hover:bg-blue-50">Import JSON</button>
                    <div className="my-1 border-t border-slate-100" />
                    <button type="button" onClick={() => { fitToContent(); setOpenMenu(null) }} className="flex w-full px-4 py-1.5 text-left text-sm text-slate-700 hover:bg-blue-50">Fit to View</button>
                    <button type="button" onClick={() => { setDeleteAllAction('canvas'); setOpenMenu(null) }} disabled={elements.length === 0} className="flex w-full px-4 py-1.5 text-left text-sm text-red-600 hover:bg-red-50 disabled:opacity-40">Clear Canvas</button>
                  </>
                )}
                {menu === 'Edit' && (
                  <>
                    <button type="button" onClick={() => { history.undo(); setOpenMenu(null) }} disabled={!history.canUndo} className="flex w-full px-4 py-1.5 text-left text-sm text-slate-700 hover:bg-blue-50 disabled:opacity-40">Undo <span className="ml-auto text-xs text-slate-400">Ctrl+Z</span></button>
                    <button type="button" onClick={() => { history.redo(); setOpenMenu(null) }} disabled={!history.canRedo} className="flex w-full px-4 py-1.5 text-left text-sm text-slate-700 hover:bg-blue-50 disabled:opacity-40">Redo <span className="ml-auto text-xs text-slate-400">Ctrl+Shift+Z</span></button>
                    <div className="my-1 border-t border-slate-100" />
                    <button type="button" onClick={() => { deleteSelected(); setOpenMenu(null) }} disabled={selectedIds.length === 0} className="flex w-full px-4 py-1.5 text-left text-sm text-slate-700 hover:bg-red-50 disabled:opacity-40">Delete <span className="ml-auto text-xs text-slate-400">Del</span></button>
                    <div className="my-1 border-t border-slate-100" />
                    <button type="button" onClick={() => { selectAll(); setOpenMenu(null) }} disabled={elements.length === 0} className="flex w-full px-4 py-1.5 text-left text-sm text-slate-700 hover:bg-blue-50 disabled:opacity-40">Select All <span className="ml-auto text-xs text-slate-400">Ctrl+A</span></button>
                    <button type="button" onClick={() => { setSelectedIds([]); setOpenMenu(null) }} disabled={selectedIds.length === 0} className="flex w-full px-4 py-1.5 text-left text-sm text-slate-700 hover:bg-blue-50 disabled:opacity-40">Deselect <span className="ml-auto text-xs text-slate-400">Esc</span></button>
                  </>
                )}
                {menu === 'Template' && (
                  <>
                    {TEMPLATES.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => { placeTemplate(template); setOpenMenu(null) }}
                        className="flex w-full flex-col px-4 py-1.5 text-left hover:bg-blue-50"
                      >
                        <span className="text-sm text-slate-700">{template.label}</span>
                        <span className="text-[10px] text-slate-400">{template.description}</span>
                      </button>
                    ))}
                  </>
                )}
                {menu === 'Scale' && (
                  <>
                    <button type="button" onClick={() => { toggleScaleMode(); setOpenMenu(null) }} className={`flex w-full px-4 py-1.5 text-left text-sm ${scaleMode ? 'bg-blue-50 font-semibold text-blue-700' : 'text-slate-700 hover:bg-blue-50'}`}>
                      {scaleMode ? 'Setting Scale…' : 'Set Scale'}
                    </button>
                    <div className="my-1 border-t border-slate-100" />
                    <button type="button" onClick={() => { setShowLegend((on) => !on); setOpenMenu(null) }} className="flex w-full px-4 py-1.5 text-left text-sm text-slate-700 hover:bg-blue-50">
                      {showLegend ? 'Hide' : 'Show'} Legend
                    </button>
                    <button type="button" onClick={() => { setShowWarnings((on) => !on); setOpenMenu(null) }} className="flex w-full px-4 py-1.5 text-left text-sm text-slate-700 hover:bg-blue-50">
                      {showWarnings ? 'Hide' : 'Show'} Warnings
                    </button>
                    {calibration && (
                      <>
                        <div className="my-1 border-t border-slate-100" />
                        <button type="button" onClick={() => { clearScale(); setOpenMenu(null) }} className="flex w-full px-4 py-1.5 text-left text-sm text-red-600 hover:bg-red-50">Clear Scale</button>
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        ))}
        <div className="ml-auto flex items-center gap-2">
          {singleSelection && (
            <button
              type="button"
              onClick={() => setShowProps((on) => !on)}
              title="Toggle properties panel"
              className={`rounded px-2 py-1 text-xs font-semibold transition ${showProps ? 'bg-blue-100 text-blue-700' : 'text-slate-500 hover:bg-slate-100'}`}
            >
              Props
            </button>
          )}
          <span className="hidden rounded bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500 sm:inline">
            {calibration ? `1 m = ${Math.round(calibration.pixelsPerMeter)} px` : 'Not to scale'}
          </span>
          <button
            type="button"
            onClick={() => setShowHelp((s) => !s)}
            className={`flex h-5 w-5 items-center justify-center rounded-full border text-[10px] font-bold ${showHelp ? 'border-blue-400 bg-blue-100 text-blue-700' : 'border-slate-200 text-slate-400 hover:border-blue-300 hover:text-blue-600'}`}
            title={showHelp ? 'Hide help' : 'Show help'}
          >?</button>
        </div>
      </div>
      {/* Click-away overlay to close menus */}
      {(openMenu || gearSubtypeMenu) && (
        <div className="fixed inset-0 z-20" onClick={() => { setOpenMenu(null); setGearSubtypeMenu(null); setShowRopeRoleMenu(false) }} />
      )}

      {/* ── BAR 2 — Ribbon / tool strip ────────────────────── */}
      <div ref={ribbonRef} className="absolute left-0 top-8 z-20 flex min-h-16 w-full flex-wrap content-center items-center gap-0 border-b border-slate-200 bg-white/95 px-2 backdrop-blur">
        {/* Draw Tools group — Rope button has a toggleable dropdown for role selection */}
        <div className="flex flex-wrap items-center gap-0.5 pr-2">
          {TOOL_LABELS.map((item) => (
            <div key={item.tool} className="relative">
              <button
                type="button"
                onClick={() => {
                  if (item.tool === 'rope' && tool === 'rope') {
                    setShowRopeRoleMenu((prev) => !prev)
                  } else {
                    setTool(item.tool)
                    if (item.tool !== 'select') setSelectedIds([])
                    setShowRopeRoleMenu(item.tool === 'rope')
                  }
                  setOpenMenu(null)
                  setGearSubtypeMenu(null)
                }}
                className={`rounded px-2.5 py-1.5 text-sm font-semibold transition ${tool === item.tool ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                title={item.label}
              >
                {item.label}
                {item.tool === 'rope' && (
                  <span className="ml-1 inline-block h-0 w-3 align-middle" style={{ borderTop: `2px ${getRopeRoleStyle(ropeRole)?.dash ? 'dashed' : 'solid'} ${tool === 'rope' ? '#fff' : (getRopeRoleStyle(ropeRole)?.color ?? '#111')}` }} />
                )}
              </button>
              {/* Rope role dropdown popover */}
              {item.tool === 'rope' && tool === 'rope' && showRopeRoleMenu && (
                <div className="absolute left-0 top-full z-30 mt-1 min-w-[140px] rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                  {ROPE_ROLES.map((style) => (
                    <button
                      key={style.role}
                      type="button"
                      onClick={() => { setRopeRole(style.role); setShowRopeRoleMenu(false) }}
                      title={style.description}
                      className={`flex w-full items-center gap-2 px-3 py-1.5 text-xs font-semibold transition ${ropeRole === style.role ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-100'}`}
                    >
                      <span className="inline-block h-0 w-4 shrink-0" style={{ borderTop: `2px ${style.dash ? 'dashed' : 'solid'} ${style.color}` }} />
                      {style.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        {/* Divider */}
        <div className="mx-1 h-6 w-px bg-slate-200" />
        {/* Gear group — click toggles subtype dropdown for anchor/pulley/descender */}
        <div className="flex flex-wrap items-center gap-0.5 overflow-visible">
          {GEAR_PALETTE.map((gear) => {
            const iconId = GEAR_TO_ICON[gear.type]
            const iconDef = iconId ? ICON_MAP[iconId] : undefined
            const hasSubtypes = GEAR_TYPES_WITH_SUBTYPES.has(gear.type)
            const subtypes = hasSubtypes ? GEAR_SUBTYPE_CATALOG[gear.type] ?? [] : []
            return (
              <Fragment key={gear.type}>
              <div className="relative shrink-0">
                <button
                    type="button"
                    onClick={() => {
                      if (hasSubtypes) {
                        setGearSubtypeMenu((prev) => prev === gear.type ? null : gear.type)
                        setOpenMenu(null)
                        setShowRopeRoleMenu(false)
                      } else {
                        addGear(gear.type)
                        setOpenMenu(null)
                        setGearSubtypeMenu(null)
                        setShowRopeRoleMenu(false)
                      }
                    }}
                    className={`flex flex-col items-center rounded px-2 py-1 text-sm font-medium transition ${gearSubtypeMenu === gear.type ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                    title={`${gear.label} (${gear.snapPoints.length} snaps)`}
                  >
                    {iconDef && (
                      <span
                        className="mb-0.5 h-5 w-5 shrink-0 [&>svg]:h-full [&>svg]:w-full"
                        dangerouslySetInnerHTML={{ __html: iconDef.svg }}
                      />
                    )}
                    <span className="leading-tight">{gear.label}</span>
                  </button>
                {/* Subtype dropdown popover */}
                {gearSubtypeMenu === gear.type && subtypes.length > 0 && (
                  <div className="absolute left-0 top-full z-30 mt-1 min-w-[160px] rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                    {/* Default (no subtype) */}
                    <button
                      type="button"
                      onClick={() => { addGear(gear.type); setGearSubtypeMenu(null) }}
                      className="flex w-full items-center gap-2 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100"
                    >
                      <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: gear.color }} />
                      {gear.label} (default)
                    </button>
                    <div className="my-0.5 border-t border-slate-100" />
                    {subtypes.map((st) => (
                      <button
                        key={st.id}
                        type="button"
                        onClick={() => { addGear(gear.type, st.id); setGearSubtypeMenu(null) }}
                        className="flex w-full items-center gap-2 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100"
                      >
                        <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: st.color }} />
                        {st.label}
                        {st.efficiency != null && (
                          <span className="ml-auto text-[10px] font-normal text-slate-400">{Math.round(st.efficiency * 100)}%</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {gear.type === 'anchor' && (
                <button
                  type="button"
                  onClick={() => { setTool('line'); setSelectedIds([]); setOpenMenu(null); setGearSubtypeMenu(null); setShowRopeRoleMenu(false) }}
                  className={`shrink-0 flex flex-col items-center rounded px-2 py-1 text-sm font-medium transition ${tool === 'line' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                  title="Line"
                >
                  <span className="mb-0.5 h-5 w-5 shrink-0 flex items-center justify-center text-xs">╱</span>
                  <span className="leading-tight">Line</span>
                </button>
              )}
              </Fragment>
            )
          })}
        </div>
      </div>

      {/* ── Floating Properties Panel ──────────────────────── */}
      {showProps && singleSelection && (
        <div className="absolute right-3 z-30 w-64 rounded-xl border border-slate-200 bg-white/95 p-3 shadow-xl backdrop-blur" style={{ top: MENUBAR_HEIGHT + ribbonHeight + 12 }}>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Properties</span>
            <button type="button" onClick={() => setShowProps(false)} className="text-xs text-slate-400 hover:text-slate-600">×</button>
          </div>
          <PropertiesPanel
            element={singleSelection}
            onUpdate={updateElementById}
            onBeginEditText={beginEditText}
          />
        </div>
      )}

      {/* Help overlay */}
      {showHelp && (
        <div className="absolute left-3 z-30 max-w-sm rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-900 shadow-lg" style={{ top: MENUBAR_HEIGHT + ribbonHeight + 12 }}>
          <div className="font-bold">Keyboard &amp; mouse shortcuts</div>
          <ul className="mt-1 list-disc space-y-1 pl-4">
            <li>Rope endpoints snap to gear contact points.</li>
            <li>Moving gear updates attached ropes automatically.</li>
            <li>Drop a gear piece onto a rope to insert it as a contact.</li>
            <li>Alt-drag or middle-drag pans; wheel zooms.</li>
            <li>Click to select; Shift/Ctrl-click toggles. Drag empty canvas for marquee select.</li>
            <li>Drag corners to resize, top handle to rotate.</li>
            <li>Double-click a text element to edit inline.</li>
            <li>Delete / Backspace removes selection. Ctrl/Cmd+Z undo, Ctrl/Cmd+Shift+Z redo.</li>
            <li>Select a rope to drag endpoints / contacts; drag a midpoint &ldquo;+&rdquo; onto a snap to add a contact.</li>
          </ul>
        </div>
      )}

      <input ref={fileInputRef} type="file" accept="application/json,.json" onChange={handleImportFile} className="hidden" />

      <div className="absolute bottom-0 left-0 right-0" style={{ top: MENUBAR_HEIGHT + ribbonHeight }}>
      <Stage
        ref={stageRef}
        width={containerSize.width}
        height={Math.max(containerSize.height - MENUBAR_HEIGHT - ribbonHeight, 1)}
        x={viewport.x}
        y={viewport.y}
        scaleX={viewport.scale}
        scaleY={viewport.scale}
        onMouseDown={handleStageMouseDown}
        onTouchStart={handleStageMouseDown}
        onMouseMove={handleStageMouseMove}
        onTouchMove={handleStageMouseMove}
        onMouseUp={handleStageMouseUp}
        onTouchEnd={handleStageMouseUp}
        onWheel={handleWheel}
        className={tool === 'select' && !scaleMode ? 'cursor-default' : 'cursor-crosshair'}
      >
        <Layer ref={bgLayerRef}>
          <Rect name="canvas-background" x={-4000} y={-4000} width={8000} height={8000} fill="#f8fafc" />
          <Grid />
        </Layer>
        <Layer>
          <Group ref={contentGroupRef}>
            {elements.map((element) => {
              if (element.kind === 'rope') {
                return (
                  <RopeNode
                    key={element.id}
                    rope={element}
                    gears={gears}
                    selected={isSelected(element.id)}
                    onSelect={handleSelect}
                    handleDrag={ropeHandleDrag}
                    onHandleDragStart={handleRopeHandleDragStart}
                    onHandleDragMove={handleRopeHandleDragMove}
                    onHandleDragEnd={handleRopeHandleDragEnd}
                  />
                )
              }
              if (element.kind === 'gear') {
                return (
                  <GearNode
                    key={element.id}
                    element={element}
                    selected={isSelected(element.id)}
                    onSelect={handleSelect}
                    onDragStart={handleElementDragStart}
                    onDragMove={handleElementDragMove}
                    onDragEnd={handleElementDragEnd}
                    registerNodeRef={registerNodeRef}
                    draggable={tool === 'select' && !scaleMode}
                  />
                )
              }
              if (element.kind === 'freedraw') {
                return (
                  <Line
                    key={element.id}
                    points={element.points}
                    stroke={element.stroke}
                    strokeWidth={element.strokeWidth}
                    lineCap="round"
                    lineJoin="round"
                    listening={tool === 'select'}
                    onClick={(e) => handleSelect(element.id, isAdditive(e))}
                    onTap={(e) => handleSelect(element.id, isAdditive(e))}
                  />
                )
              }
              return (
                <BasicShapeNode
                  key={element.id}
                  element={element}
                  selected={isSelected(element.id)}
                  onSelect={handleSelect}
                  onDragStart={handleElementDragStart}
                  onDragMove={handleElementDragMove}
                  onDragEnd={handleElementDragEnd}
                  onDoubleClickText={beginEditText}
                  registerNodeRef={registerNodeRef}
                  draggable={tool === 'select' && !scaleMode}
                />
              )
            })}
          </Group>
          {draftRopeElement && <RopeNode rope={draftRopeElement} gears={gears} selected={false} onSelect={() => undefined} />}
          {draftShapeElement && <BasicShapeNode element={draftShapeElement} selected={false} onSelect={() => undefined} />}
          {draftFreedraw && (
            <Line
              points={draftFreedraw.points}
              stroke="#111827"
              strokeWidth={3}
              lineCap="round"
              lineJoin="round"
              listening={false}
            />
          )}
          {marqueeBox && (
            <Rect
              x={marqueeBox.minX}
              y={marqueeBox.minY}
              width={marqueeBox.maxX - marqueeBox.minX}
              height={marqueeBox.maxY - marqueeBox.minY}
              fill="rgba(37,99,235,0.08)"
              stroke="#2563eb"
              strokeWidth={1}
              dash={[4, 4]}
              listening={false}
            />
          )}
          <Group ref={overlayGroupRef}>
            {showMeasurements && (
              <MeasurementLayer elements={elements} gears={gears} calibration={calibration} zoom={viewport.scale} />
            )}
            {showWarnings && (
              <WarningLayer elements={elements} gears={gears} zoom={viewport.scale} />
            )}
          </Group>
          {draftScale && (() => {
            const dist = Math.hypot(draftScale.current.x - draftScale.start.x, draftScale.current.y - draftScale.start.y)
            const midX = (draftScale.start.x + draftScale.current.x) / 2
            const midY = (draftScale.start.y + draftScale.current.y) / 2
            const fs = 13 / Math.max(viewport.scale, 0.05)
            return (
              <>
                <Line
                  points={[draftScale.start.x, draftScale.start.y, draftScale.current.x, draftScale.current.y]}
                  stroke="#2563eb"
                  strokeWidth={2 / Math.max(viewport.scale, 0.05)}
                  dash={[8 / Math.max(viewport.scale, 0.05), 6 / Math.max(viewport.scale, 0.05)]}
                  listening={false}
                />
                <Text
                  x={midX}
                  y={midY - fs * 1.6}
                  text={calibration ? formatLength(dist, calibration) : `${Math.round(dist)} px`}
                  fill="#2563eb"
                  fontSize={fs}
                  fontStyle="bold"
                  listening={false}
                />
              </>
            )
          })()}
          <SnapHighlight candidate={snapCandidate} />
          <Transformer
            ref={transformerRef}
            rotateEnabled
            keepRatio={transformerConfig.keepRatio}
            enabledAnchors={transformerConfig.enabledAnchors}
            ignoreStroke
            anchorSize={9}
            anchorStroke="#2563eb"
            anchorFill="#ffffff"
            anchorCornerRadius={2}
            borderStroke="#2563eb"
            borderDash={[3, 3]}
            rotateAnchorOffset={26}
            padding={4}
            shouldOverdrawWholeArea
            boundBoxFunc={(oldBox, newBox) => {
              if (Math.abs(newBox.width) < 6 || Math.abs(newBox.height) < 6) return oldBox
              return newBox
            }}
            onTransform={handleTransform}
            onTransformStart={handleTransformStart}
            onTransformEnd={handleTransformEnd}
            onDblClick={() => {
              if (selectedIds.length === 1) {
                const el = elements.find((e) => e.id === selectedIds[0])
                if (el && el.kind === 'shape' && el.shapeType === 'text') {
                  beginEditText(el.id)
                }
              }
            }}
            onDblTap={() => {
              if (selectedIds.length === 1) {
                const el = elements.find((e) => e.id === selectedIds[0])
                if (el && el.kind === 'shape' && el.shapeType === 'text') {
                  beginEditText(el.id)
                }
              }
            }}
          />
          {/* ── Endpoint handles for selected line/arrow ──────── */}
          {tool === 'select' && !scaleMode && selectedIds.length === 1 && (() => {
            const el = elements.find((e) => e.id === selectedIds[0])
            if (!el || el.kind !== 'shape' || (el.shapeType !== 'line' && el.shapeType !== 'arrow')) return null
            const handleRadius = 6 / Math.max(viewport.scale, 0.05)
            const strokeWidth = 1.5 / Math.max(viewport.scale, 0.05)
            return (
              <>
                {/* Dashed line showing the current shape path */}
                <Line
                  points={[el.x, el.y, el.x + el.width, el.y + el.height]}
                  stroke="#2563eb"
                  strokeWidth={1.5 / Math.max(viewport.scale, 0.05)}
                  dash={[6 / Math.max(viewport.scale, 0.05), 4 / Math.max(viewport.scale, 0.05)]}
                  listening={false}
                />
                {/* Start-point handle */}
                <Circle
                  x={el.x}
                  y={el.y}
                  radius={handleRadius}
                  fill="#ffffff"
                  stroke="#2563eb"
                  strokeWidth={strokeWidth}
                  draggable
                  onDragStart={() => handleEndpointDragStart(el.id)}
                  onDragMove={(evt) => {
                    const node = evt.target
                    handleEndpointDrag(el.id, 'start', node.x(), node.y())
                  }}
                  onDragEnd={(evt) => {
                    const node = evt.target
                    handleEndpointDrag(el.id, 'start', node.x(), node.y())
                    handleEndpointDragEnd()
                  }}
                />
                {/* End-point handle */}
                <Circle
                  x={el.x + el.width}
                  y={el.y + el.height}
                  radius={handleRadius}
                  fill="#ffffff"
                  stroke="#2563eb"
                  strokeWidth={strokeWidth}
                  draggable
                  onDragStart={() => handleEndpointDragStart(el.id)}
                  onDragMove={(evt) => {
                    const node = evt.target
                    handleEndpointDrag(el.id, 'end', node.x(), node.y())
                  }}
                  onDragEnd={(evt) => {
                    const node = evt.target
                    handleEndpointDrag(el.id, 'end', node.x(), node.y())
                    handleEndpointDragEnd()
                  }}
                />
              </>
            )
          })()}
        </Layer>
      </Stage>
      </div>
      {editingText && (() => {
        const target = elements.find((entry) => entry.id === editingText.id)
        if (!target || target.kind !== 'shape' || target.shapeType !== 'text') return null
        const screenX = target.x * viewport.scale + viewport.x
        const screenY = target.y * viewport.scale + viewport.y + MENUBAR_HEIGHT + ribbonHeight
        const width = Math.max(120, Math.abs(target.width) * viewport.scale)
        const fontSize = (target.fontSize ?? 20) * viewport.scale
        return (
          <textarea
            ref={textareaRef}
            value={editingText.value}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setEditingText((current) => current ? { ...current, value: event.target.value } : current)}
            onBlur={commitEditText}
            onKeyDown={(event: ReactKeyboardEvent<HTMLTextAreaElement>) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                commitEditText()
              } else if (event.key === 'Escape') {
                event.preventDefault()
                cancelEditText()
              }
            }}
            style={{
              position: 'absolute',
              left: screenX,
              top: screenY,
              width,
              minHeight: fontSize * 1.4,
              fontSize,
              color: target.stroke,
              lineHeight: 1.2,
              border: '1px solid #2563eb',
              outline: 'none',
              borderRadius: 4,
              padding: '2px 4px',
              background: 'rgba(255,255,255,0.97)',
              resize: 'none',
              transform: target.rotation ? `rotate(${target.rotation}deg)` : undefined,
              transformOrigin: 'top left',
              zIndex: 30,
              fontFamily: 'inherit',
            }}
          />
        )
      })()}
      {scaleMode && (
        <div className="pointer-events-none absolute left-1/2 z-30 -translate-x-1/2 rounded-full bg-blue-600 px-4 py-1.5 text-sm font-semibold text-white shadow-lg" style={{ top: MENUBAR_HEIGHT + ribbonHeight + 12 }}>
          Drag across a known distance, then enter its length in metres. (Esc to cancel)
        </div>
      )}
      {showLegend && <RiggingLegend elements={elements} />}
      <div className="pointer-events-none absolute bottom-4 left-4 z-10 flex flex-col gap-1 rounded-lg bg-white/90 px-3 py-2 text-xs font-semibold text-slate-600 shadow ring-1 ring-slate-200">
        {calibration ? (() => {
          const bar = niceScaleBar(calibration, viewport.scale)
          return (
            <>
              <div className="flex items-center gap-2">
                <div className="relative h-2 border-x-2 border-b-2 border-slate-700" style={{ width: Math.max(8, bar.px) }} />
                <span>{bar.meters} m</span>
              </div>
              <span className="text-[11px] font-normal text-slate-400">1 m = {Math.round(calibration.pixelsPerMeter)} px</span>
            </>
          )
        })() : (
          <span className="font-normal text-slate-400">Not to scale — use Set Scale</span>
        )}
      </div>
      <AlertDialog open={deleteAllAction !== null} onOpenChange={(open) => { if (!open) setDeleteAllAction(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear the entire canvas?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes every diagram element. The cleared canvas will not be saved until you make another edit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteAll} className="bg-red-600 hover:bg-red-700">
              Clear canvas
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function Grid() {
  const lines = []
  for (let x = -4000; x <= 4000; x += 40) {
    lines.push(<Line key={`x-${x}`} points={[x, -4000, x, 4000]} stroke="#e2e8f0" strokeWidth={1} />)
  }
  for (let y = -4000; y <= 4000; y += 40) {
    lines.push(<Line key={`y-${y}`} points={[-4000, y, 4000, y]} stroke="#e2e8f0" strokeWidth={1} />)
  }
  return <>{lines}</>
}

type PropertiesPanelProps = {
  element: DrawingElement | null
  onUpdate: (id: string, updater: (element: DrawingElement) => DrawingElement) => void
  onBeginEditText: (id: string) => void
}

const SHAPE_FILL_PRESETS: Array<{ label: string; value: string | undefined }> = [
  { label: 'None', value: undefined },
  { label: 'Slate', value: 'rgba(148,163,184,0.18)' },
  { label: 'Amber', value: 'rgba(251,191,36,0.18)' },
  { label: 'Emerald', value: 'rgba(34,197,94,0.18)' },
  { label: 'Blue', value: 'rgba(59,130,246,0.18)' },
  { label: 'Rose', value: 'rgba(244,63,94,0.18)' },
]

function PropertiesPanel({ element, onUpdate, onBeginEditText }: PropertiesPanelProps) {
  if (!element) {
    return (
      <section>
        <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Properties</h2>
        <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-500">
          Select a single element to edit its color, size, rotation, and other properties.
        </p>
      </section>
    )
  }

  if (element.kind === 'rope') {
    return (
      <section>
        <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Rope properties</h2>
        <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
          <div>
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">Role</label>
            <div className="grid grid-cols-2 gap-1.5">
              <button
                type="button"
                title="No role assigned"
                onClick={() => onUpdate(element.id, (current) => {
                  if (current.kind !== 'rope') return current
                  const { role: _removed, ...rest } = current
                  return rest as typeof current
                })}
                className={`flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-xs font-semibold transition ${element.role == null ? 'border-blue-600 bg-blue-50 text-slate-900' : 'border-slate-200 bg-white text-slate-600 hover:border-blue-300'}`}
              >
                <span className="inline-block h-0 w-4 shrink-0 rounded" style={{ borderTop: '3px solid #94a3b8' }} />
                None
              </button>
              {ROPE_ROLES.map((style) => (
                <button
                  key={style.role}
                  type="button"
                  title={style.description}
                  onClick={() => onUpdate(element.id, (current) => {
                    if (current.kind !== 'rope') return current
                    const prevDefault = getRopeRoleStyle(current.role)?.color
                    const colorIsDefault = prevDefault != null ? current.color === prevDefault : isRoleDefaultColor(current.color)
                    return { ...current, role: style.role, color: colorIsDefault ? style.color : current.color }
                  })}
                  className={`flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-xs font-semibold transition ${element.role === style.role ? 'border-blue-600 bg-blue-50 text-slate-900' : 'border-slate-200 bg-white text-slate-600 hover:border-blue-300'}`}
                >
                  <span
                    className="inline-block h-0 w-4 shrink-0 rounded"
                    style={{ borderTop: `3px ${style.dash ? 'dashed' : 'solid'} ${style.color}` }}
                  />
                  {style.label}
                </button>
              ))}
            </div>
          </div>
          <ColorField
            label="Rope color"
            value={element.color}
            onChange={(value) => onUpdate(element.id, (current) => current.kind === 'rope' ? { ...current, color: value } : current)}
          />
          <NumberField
            label="Line thickness"
            min={1}
            max={20}
            step={0.5}
            value={element.diameter}
            onChange={(value) => onUpdate(element.id, (current) => current.kind === 'rope' ? { ...current, diameter: value } : current)}
            suffix="px"
          />
          <NumberField
            label="Rope diameter"
            min={0}
            max={100}
            step={0.5}
            value={element.ropeDiameterMm ?? 0}
            onChange={(value) => onUpdate(element.id, (current) => current.kind === 'rope' ? { ...current, ropeDiameterMm: value || undefined } : current)}
            suffix="mm"
          />
          {!element.autoSag && (
            <div>
              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                Tension <span className="font-normal text-slate-400">(sag)</span>
              </label>
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={Math.round((1 - (element.sagMultiplier ?? 1)) * 100)}
                onChange={(event) => {
                  const tension = parseInt(event.target.value, 10)
                  const sagMul = 1 - tension / 100
                  onUpdate(element.id, (current) => current.kind === 'rope' ? { ...current, sagMultiplier: sagMul } : current)
                }}
                className="w-full accent-blue-600"
              />
              <div className="mt-0.5 flex justify-between text-[9px] text-slate-400">
                <span>Loose</span>
                <span>Taut</span>
              </div>
            </div>
          )}
        </div>
      </section>
    )
  }

  if (element.kind === 'gear') {
    return (
      <section>
        <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Gear properties</h2>
        <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
          <div>
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">Label</label>
            <input
              type="text"
              value={element.label ?? ''}
              placeholder={GEAR_DEFINITIONS[element.gearType].label}
              onChange={(event) => onUpdate(element.id, (current) => current.kind === 'gear' ? { ...current, label: event.target.value } : current)}
              className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-800 outline-none focus:border-blue-400"
            />
          </div>
          {GEAR_TYPES_WITH_SUBTYPES.has(element.gearType) && (() => {
            const subtypes = GEAR_SUBTYPE_CATALOG[element.gearType] ?? []
            return (
              <div>
                <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">Type</label>
                <div className="grid grid-cols-2 gap-1.5">
                  <button
                    type="button"
                    title="Default type"
                    onClick={() => onUpdate(element.id, (current) => {
                      if (current.kind !== 'gear') return current
                      const { subtype: _removed, ...rest } = current
                      return rest as typeof current
                    })}
                    className={`flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-xs font-semibold transition ${element.subtype == null ? 'border-blue-600 bg-blue-50 text-slate-900' : 'border-slate-200 bg-white text-slate-600 hover:border-blue-300'}`}
                  >
                    <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: GEAR_DEFINITIONS[element.gearType].color }} />
                    Default
                  </button>
                  {subtypes.map((st) => (
                    <button
                      key={st.id}
                      type="button"
                      title={st.efficiency != null ? `Efficiency: ${Math.round(st.efficiency * 100)}%` : st.label}
                      onClick={() => onUpdate(element.id, (current) => current.kind === 'gear' ? { ...current, subtype: st.id } : current)}
                      className={`flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-xs font-semibold transition ${element.subtype === st.id ? 'border-blue-600 bg-blue-50 text-slate-900' : 'border-slate-200 bg-white text-slate-600 hover:border-blue-300'}`}
                    >
                      <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: st.color }} />
                      {st.label}
                      {st.efficiency != null && (
                        <span className="ml-auto text-[10px] font-normal text-slate-400">{Math.round(st.efficiency * 100)}%</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )
          })()}
          <NumberField
            label="Width"
            min={20}
            max={400}
            step={1}
            value={Math.round(element.width)}
            onChange={(value) => onUpdate(element.id, (current) => current.kind === 'gear' ? { ...current, width: value } : current)}
            suffix="px"
          />
          <NumberField
            label="Height"
            min={20}
            max={400}
            step={1}
            value={Math.round(element.height)}
            onChange={(value) => onUpdate(element.id, (current) => current.kind === 'gear' ? { ...current, height: value } : current)}
            suffix="px"
          />
          <NumberField
            label="Rotation"
            min={-360}
            max={360}
            step={1}
            value={Math.round(element.rotation)}
            onChange={(value) => onUpdate(element.id, (current) => current.kind === 'gear' ? { ...current, rotation: value } : current)}
            suffix="°"
          />
        </div>
      </section>
    )
  }

  if (element.kind === 'freedraw') {
    return (
      <section>
        <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Pen stroke properties</h2>
        <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
          <ColorField
            label="Stroke"
            value={element.stroke}
            onChange={(value) => onUpdate(element.id, (current) => current.kind === 'freedraw' ? { ...current, stroke: value } : current)}
          />
          <NumberField
            label="Width"
            min={1}
            max={20}
            step={1}
            value={element.strokeWidth}
            onChange={(value) => onUpdate(element.id, (current) => current.kind === 'freedraw' ? { ...current, strokeWidth: value } : current)}
            suffix="px"
          />
        </div>
      </section>
    )
  }

  const shape = element
  const isTextShape = shape.shapeType === 'text'
  const supportsFill = shape.shapeType === 'rect' || shape.shapeType === 'ellipse'

  return (
    <section>
      <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Shape properties</h2>
      <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
        {isTextShape && (
          <>
            <div>
              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">Text</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={shape.text ?? ''}
                  onChange={(event) => onUpdate(shape.id, (current) => current.kind === 'shape' && current.shapeType === 'text' ? { ...current, text: event.target.value } : current)}
                  className="flex-1 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-800 outline-none focus:border-blue-400"
                />
                <button
                  type="button"
                  onClick={() => onBeginEditText(shape.id)}
                  title="Edit text on canvas"
                  className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs font-semibold text-slate-600 hover:border-blue-300 hover:bg-blue-50"
                >
                  Inline
                </button>
              </div>
            </div>
            <NumberField
              label="Font size"
              min={6}
              max={144}
              step={1}
              value={Math.round(shape.fontSize ?? 20)}
              onChange={(value) => onUpdate(shape.id, (current) => current.kind === 'shape' && current.shapeType === 'text' ? { ...current, fontSize: value } : current)}
              suffix="px"
            />
          </>
        )}

        {shape.shapeType !== 'i-beam' && (
          <ColorField
            label={isTextShape ? 'Text color' : 'Stroke'}
            value={shape.stroke}
            onChange={(value) => onUpdate(shape.id, (current) => current.kind === 'shape' ? { ...current, stroke: value } : current)}
          />
        )}

        {supportsFill && (
          <div>
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">Fill</label>
            <div className="flex flex-wrap gap-1.5">
              {SHAPE_FILL_PRESETS.map((preset) => {
                const isActive = (shape.fill ?? undefined) === preset.value
                return (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => onUpdate(shape.id, (current) => current.kind === 'shape' ? { ...current, fill: preset.value } : current)}
                    className={`flex h-7 w-7 items-center justify-center rounded-md border text-[10px] font-semibold ${isActive ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200'} `}
                    style={{ background: preset.value ?? 'transparent', color: preset.value ? '#1f2937' : '#94a3b8' }}
                    title={preset.label}
                  >
                    {preset.value ? '' : '∅'}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {(shape.shapeType === 'line' || shape.shapeType === 'arrow' || shape.shapeType === 'i-beam') ? (
          <NumberField
            label="Length"
            min={1}
            max={2000}
            step={1}
            value={Math.round(Math.hypot(shape.width, shape.height))}
            onChange={(value) => onUpdate(shape.id, (current) => {
              if (current.kind !== 'shape') return current
              const oldLen = Math.hypot(current.width, current.height)
              if (oldLen < 0.01) return { ...current, width: value, height: 0 }
              const scale = value / oldLen
              return { ...current, width: roundTo(current.width * scale, 2), height: roundTo(current.height * scale, 2) }
            })}
            suffix="px"
          />
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <NumberField
              label="Width"
              min={1}
              max={2000}
              step={1}
              value={Math.round(shape.width)}
              onChange={(value) => onUpdate(shape.id, (current) => current.kind === 'shape' ? { ...current, width: value } : current)}
              suffix="px"
            />
            <NumberField
              label="Height"
              min={1}
              max={2000}
              step={1}
              value={Math.round(shape.height)}
              onChange={(value) => onUpdate(shape.id, (current) => current.kind === 'shape' ? { ...current, height: value } : current)}
              suffix="px"
            />
          </div>
        )}
        <NumberField
          label="Rotation"
          min={-360}
          max={360}
          step={1}
          value={Math.round(shape.rotation)}
          onChange={(value) => onUpdate(shape.id, (current) => current.kind === 'shape' ? { ...current, rotation: value } : current)}
          suffix="°"
        />
      </div>
    </section>
  )
}

type ColorFieldProps = {
  label: string
  value: string
  onChange: (value: string) => void
}

const STROKE_PRESETS = ['#111827', '#334155', '#2563eb', '#16a34a', '#dc2626', '#f59e0b', '#7c3aed', '#ffffff']

function ColorField({ label, value, onChange }: ColorFieldProps) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={normalizeColorForInput(value)}
          onChange={(event) => onChange(event.target.value)}
          className="h-8 w-9 cursor-pointer rounded-md border border-slate-200 bg-white"
          aria-label={label}
        />
        <input
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="flex-1 rounded-md border border-slate-200 bg-white px-2 py-1.5 font-mono text-xs text-slate-700 outline-none focus:border-blue-400"
        />
      </div>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {STROKE_PRESETS.map((preset) => (
          <button
            key={preset}
            type="button"
            onClick={() => onChange(preset)}
            className={`h-5 w-5 rounded border ${value.toLowerCase() === preset.toLowerCase() ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200'}`}
            style={{ background: preset }}
            title={preset}
            aria-label={`Set ${label} to ${preset}`}
          />
        ))}
      </div>
    </div>
  )
}

function normalizeColorForInput(value: string): string {
  if (value.startsWith('#')) return value.length === 7 ? value : '#000000'
  return '#000000'
}

type NumberFieldProps = {
  label: string
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  step?: number
  suffix?: string
}

function NumberField({ label, value, onChange, min, max, step = 1, suffix }: NumberFieldProps) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</label>
      <div className="flex items-center gap-1">
        <input
          type="number"
          value={Number.isFinite(value) ? value : 0}
          onChange={(event) => {
            const parsed = Number.parseFloat(event.target.value)
            if (Number.isFinite(parsed)) onChange(parsed)
          }}
          min={min}
          max={max}
          step={step}
          className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-800 outline-none focus:border-blue-400"
        />
        {suffix && <span className="text-xs text-slate-400">{suffix}</span>}
      </div>
    </div>
  )
}
